<template>
  <div class="clip-pool">
    <div class="pool-header-wrapper">
      <div class="header">
        <h3>{{ formatTime(totalDuration) }}</h3>
        <div class="header-actions">
          <button 
            v-if="isFineTuning"
            class="action-btn active-mode" 
            @click="fineTuningGroupId = null"
            title="退出精剪模式"
          >
            <i class="mdi mdi-close-circle-outline"></i> 退出精剪
          </button>
          <button class="action-btn danger" @click="clearPool" :disabled="totalSegments === 0" title="清空剪辑池">
            <i class="mdi mdi-delete-sweep"></i> 清空
          </button>
          <button class="action-btn" @click="createNewGroup" title="新建组">
            <i class="mdi mdi-plus"></i> 新建组
          </button>
          
          <!-- AI 助手下拉菜单 -->
          <div v-if="isAiAllowed" class="dropdown ai-dropdown-container">
            <button 
              class="action-btn ai-btn" 
              :disabled="totalSegments === 0 || isAiProcessingAny" 
              title="AI 功能集合"
            >
              <i class="mdi" :class="isAiProcessingAny ? 'mdi-loading mdi-spin' : 'mdi-auto-fix'" style="font-size: 14px;"></i>
              <span>AI助手</span>
              <i class="mdi mdi-chevron-down" style="font-size: 14px;"></i>
            </button>
            <div class="dropdown-content">
              <a @click="aiOrchestrate" title="逻辑重构与自动分组">
                <i class="mdi mdi-family-tree" style="margin-right: 4px;"></i> 智能编排
              </a>
              <a @click="aiGenerateTitles" title="根据内容生成爆款标题">
                <i class="mdi mdi-format-title" style="margin-right: 4px;"></i> 生成标题
              </a>
            </div>

            <!-- 标题参数输入 Popover -->
            <div v-if="showTitleParamsDialog" class="ai-params-popover">
              <div class="popover-header">
                <span><i class="mdi mdi-auto-fix"></i> 标题生成设置</span>
                <i class="mdi mdi-close" @click.stop="showTitleParamsDialog = false"></i>
              </div>
              <div class="popover-body">
                <label>请输入推广商品名称(选填)：</label>
                <input 
                  type="text" 
                  v-model="titleParams.commodityName" 
                  placeholder="例如：高腰阔腿裤..."
                  class="commodity-input"
                  @keyup.enter="confirmGenerateTitles"
                  autofocus
                />
                <p class="hint">填写后 AI 会根据商品特性生成更精准的标题</p>
              </div>
              <div class="popover-footer">
                <button class="cancel-btn" @click.stop="showTitleParamsDialog = false">取消</button>
                <button class="confirm-btn" @click.stop="confirmGenerateTitles" :disabled="isGeneratingTitles">
                  <i v-if="isGeneratingTitles" class="mdi mdi-loading mdi-spin"></i>
                  开始生成
                </button>
              </div>
            </div>

            <!-- 编排参数输入 Popover -->
            <div v-if="showOrchestrationParamsDialog" class="ai-params-popover">
              <div class="popover-header">
                <span><i class="mdi mdi-auto-fix"></i> 智能编排设置</span>
                <i class="mdi mdi-close" @click.stop="showOrchestrationParamsDialog = false"></i>
              </div>
              <div class="popover-body">
                <label>请输入推广商品名称(选填)：</label>
                <input 
                  type="text" 
                  v-model="orchestrationParams.commodityName" 
                  placeholder="例如：高腰阔腿裤..."
                  class="commodity-input"
                  @keyup.enter="confirmAiOrchestrate"
                  autofocus
                />
                <p class="hint">填写后 AI 会根据商品特性进行更贴切的逻辑编排</p>
              </div>
              <div class="popover-footer">
                <button class="cancel-btn" @click.stop="showOrchestrationParamsDialog = false">取消</button>
                <button class="confirm-btn" @click.stop="confirmAiOrchestrate" :disabled="isOrchestrating">
                  <i v-if="isOrchestrating" class="mdi mdi-loading mdi-spin"></i>
                  开始编排
                </button>
              </div>
            </div>
          </div>

          <button class="action-btn primary" @click="exportAll" :disabled="isExporting || totalSegments === 0">
            {{ isExporting ? '...' : '导出' }}
          </button>
        </div>
      </div>
      
      <div v-if="isFineTuning" class="fine-tuning-toolbar">
           <div class="selection-count" v-if="selectedWordLocations.length > 0">
               已选 {{ selectedWordLocations.length }} 字
           </div>
           <div class="selection-count" v-else>
               请选择文字
           </div>
           <div class="ft-actions">
               <button v-if="canMerge" class="ft-btn" @click="mergeSegment()" title="与上一段合并 (Backspace)">
                  <i class="mdi mdi-backspace"></i> 合并
               </button>
               <button v-else class="ft-btn" @click="splitSegment()" :disabled="!canSplit" title="从选中字之前断开为两段 (Enter)">
                  <i class="mdi mdi-keyboard-return"></i> 分裂
               </button>
               <button class="ft-btn" @click="restoreSelectedWords" :disabled="selectedWordLocations.length === 0" title="恢复选中">
                  <i class="mdi mdi-undo"></i> 恢复
               </button>
               <button class="ft-btn delete" @click="deleteSelectedWords" :disabled="selectedWordLocations.length === 0" title="删除选中 (Del)">
                  <i class="mdi mdi-delete"></i> 删除
               </button>
           </div>
      </div>
      
      <div v-if="selectedSegmentIds.length > 0" class="batch-segment-toolbar">
           <div class="selection-count">
               已选 {{ selectedSegmentIds.length }} 段
           </div>
           <div class="batch-actions">
               <div class="dropdown move-dropdown">
                 <button class="batch-btn">
                   <i class="mdi mdi-folder-move"></i> 移至组 <i class="mdi mdi-chevron-down"></i>
                 </button>
                 <div class="dropdown-content">
                    <template v-for="(group, gIndex) in groups" :key="group.id">
                      <div class="group-option">
                        <span class="group-name-label">{{ group.name || `组${gIndex+1}` }}</span>
                        <div class="move-sub-options">
                          <a @click.stop="batchMoveToGroup(gIndex, 'start')" title="移至此组开头">开头</a>
                          <a @click.stop="batchMoveToGroup(gIndex, 'end')" title="移至此组末尾">末尾</a>
                        </div>
                      </div>
                    </template>
                    <a @click="batchMoveToNewGroup" style="border-top: 1px solid #eee; padding: 12px 16px; font-weight: bold; color: #2196f3; cursor: pointer; display: block;">
                      <i class="mdi mdi-plus"></i> 新建并移动
                    </a>
                 </div>
               </div>
               <button class="batch-btn delete" @click="batchDeleteSegments">
                  <i class="mdi mdi-delete"></i> 删除
               </button>
               <button class="batch-btn" @click="selectedSegmentIds = []">
                  取消
               </button>
           </div>
      </div>
    </div>
    
    <div class="groups-container" @click="handleContainerClick">

      <!-- 重复素材提示横幅 -->
      <div v-if="dupStats.groupCount > 0" class="dup-banner">
        <i class="mdi mdi-alert-decagram"></i>
        <span>检测到 <b>{{ dupStats.groupCount }}</b> 组重复片段（共 <b>{{ dupStats.segmentCount }}</b> 条），带 <em>重/叠</em> 标记的片段请留意删除</span>
      </div>
      
      <!-- Hot Video Source Trace Group -->
      <div 
        v-if="sourceTraceSegments.length > 0" 
        class="group-item source-trace-group"
        :class="{ 'active-mode-border': playMode === 'trace' && activePanel === 'pool' }"
      >
        <div class="group-header trace-header">
          <div class="header-left">
            <i class="mdi mdi-source-branch" style="margin-right: 5px; color: #ff9800;"></i>
            <span class="trace-title">热点视频溯源</span>
            <span class="group-count">({{ formatTime(sourceTraceDuration) }})</span>
            <button 
              class="header-icon-btn highlight-btn" 
              :class="{ 'active-highlight': highlightedTrace }"
              @click.stop="toggleTraceHighlight" 
              title="在文稿中高亮此组内容"
            >
              <i class="mdi" :class="highlightedTrace ? 'mdi-format-color-highlight' : 'mdi-format-color-text'"></i>
            </button>
          </div>
          <button class="delete-btn" @click.stop="closeSourceTrace" title="关闭溯源">×</button>
        </div>
        <div class="segments-list trace-list">
           <draggable-group 
            :segments="sourceTraceSegments" 
            :group-index="-999"
            :fine-tuning="false"
             :readonly="true"
             :enable-drag-out="true"
             :is-source-trace="true"
             :clone="cloneSegment"
             :selected-word-locations="[]"
             :selected-word-location="selectedWordLocation"
             :selected-ids="selectedSegmentIds"
             @update:selected-ids="selectedSegmentIds = $event"
             :current-time="currentTime"
            :playing-video-id="previewSource ? (previewSource.video_id || previewSource.id) : null"
            @segment-click="handleSegmentClick"
          />
        </div>
      </div>

      <!-- AI Disassembly Reference Groups -->
      <div 
        v-for="(group, ari) in aiReferenceGroups" 
        :key="'ai-dis-'+group.id"
        class="group-item ai-reference-group"
      >
        <div class="group-header ai-ref-header" @click="expandedGroups[group.id] = (expandedGroups[group.id] === false)">
          <div class="header-left">
            <i 
              class="mdi collapse-icon" 
              :class="expandedGroups[group.id] !== false ? 'mdi-chevron-down' : 'mdi-chevron-right'"
            ></i>
            <i class="mdi mdi-brain" style="margin-right: 5px; color: #673ab7;"></i>
            <span class="ai-ref-title">AI 参考源: {{ group.name || '未命名组' }}</span>
            <span class="group-count">({{ getGroupDurationInfo(group) }})</span>
            <button 
              class="header-icon-btn highlight-btn" 
              :class="{ 'active-highlight': highlightedAiRefId === group.id }"
              @click.stop="toggleAiRefHighlight(group)" 
              title="在文稿中高亮此组内容"
            >
              <i class="mdi" :class="highlightedAiRefId === group.id ? 'mdi-format-color-highlight' : 'mdi-format-color-text'"></i>
            </button>
          </div>
          <button class="header-icon-btn delete-btn" @click.stop="removeAiReferenceGroupWrap(ari)" title="移除参考源">×</button>
        </div>
        
        <div v-if="expandedGroups[group.id] !== false">
          <div v-if="disassemblyResults[group.id]" class="ai-disassembly-result">
            <div class="disassembly-header">
              <span class="disassembly-title">AI 逻辑拆解报告</span>
            </div>
            <div class="disassembly-body">
              <div v-for="(quotes, category) in disassemblyResults[group.id]" :key="category" class="dis-category">
                <div class="category-name">{{ category }}</div>
                <div class="category-quotes">
                  <div 
                    v-for="(quote, qIdx) in quotes" 
                    :key="qIdx" 
                    class="quote-item"
                    @click.stop="jumpToQuote(quote, group)"
                    :title="quote.start_time > 0 ? '点击跳转视频' : '未匹配到具体时间轴'"
                  >
                    <i class="mdi mdi-play-circle-outline quote-play-icon" v-if="quote.start_time > 0"></i>
                    <span class="quote-text">{{ quote.text }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="segments-list read-only-list">
            <draggable-group 
              :segments="group.segments || []" 
              :group-index="-888 - ari"
              :fine-tuning="false"
              :readonly="true"
              :enable-drag-out="true"
              :clone="cloneSegment"
              :selected-word-locations="[]"
              :selected-word-location="selectedWordLocation"
              :selected-ids="selectedSegmentIds"
              @update:selected-ids="selectedSegmentIds = $event"
              :current-time="currentTime"
              :playing-video-id="previewSource ? (previewSource.video_id || previewSource.id) : null"
              @segment-click="handleSegmentClick"
            />
          </div>
        </div>
      </div>

      <div v-if="groups.length === 0 && sourceTraceSegments.length === 0 && aiReferenceGroups.length === 0" class="empty-pool">
        <p>剪辑池为空</p>
        <p>点击“新建组”或直接添加素材</p>
      </div>

      <draggable 
        v-model="localGroups" 
        item-key="id"
        handle=".group-drag-handle"
        animation="200"
        :scroll="true"
        :scroll-sensitivity="150"
        class="groups-draggable"
        :class="{ 'active-mode-border': playMode === 'pool' && activePanel === 'pool' }"
        ghost-class="ghost-group"
      >
        <template #item="{ element: group, index: gIndex }">
          <div 
            class="group-item"
            :class="{ active: currentGroupIndex === gIndex }"
          >
            <div class="group-header" @click="toggleGroup(gIndex, group)">
              <div class="header-left">
                <input 
                  type="checkbox" 
                  :checked="isGroupAllSelected(group)" 
                  @change.stop="toggleGroupSelection(group)"
                  style="margin-right: 8px; cursor: pointer;"
                >
                <i class="mdi mdi-drag-vertical group-drag-handle" title="拖拽排序"></i>
                <i 
                  class="mdi collapse-icon" 
                  :class="expandedGroups[group.id] !== false ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                ></i>
                <textarea 
                  class="group-name-input"
                  :value="group.name" 
                  @click.stop="selectGroup(gIndex)"
                  @input="handleGroupNameInput(gIndex, $event)"
                  @focus="autoResizeTextarea($event)"
                  placeholder="输入组名"
                  rows="1"
                ></textarea>
                <button 
                  class="group-export-btn" 
                  @click.stop="exportSingleGroup(group)"
                  title="导出此组所有片段"
                >
                  <i class="mdi mdi-export-variant"></i>
                  {{ getGroupDurationInfo(group) }}
                </button>
                <button 
                  class="header-icon-btn highlight-btn" 
                  :class="{ 'active-highlight': highlightedGroupId === group.id }"
                  @click.stop="toggleHighlight(group)" 
                  title="在文稿中高亮此组内容"
                >
                  <i class="mdi" :class="highlightedGroupId === group.id ? 'mdi-format-color-highlight' : 'mdi-format-color-text'"></i>
                </button>
                <div v-if="isAiAllowed" style="display: inline-flex; position: relative;">
                  <button 
                    class="header-icon-btn ai-disassemble-btn" 
                    @click.stop="disassembleGroup(gIndex)"
                    title="AI 逻辑拆解 (分析钩子、痛点等)"
                    :disabled="isDisassembling[group.id] || !group.segments || group.segments.length === 0"
                  >
                    <i class="mdi" :class="isDisassembling[group.id] ? 'mdi-loading mdi-spin' : 'mdi-brain'"></i>
                  </button>

                  <!-- 逻辑拆解设置 Popover (原地弹出) -->
                  <div v-if="showDisassembleParamsDialog && disassemblingGroupId === group.id" class="ai-params-popover disassemble-popover" @click.stop>
                    <div class="popover-header">
                      <span><i class="mdi mdi-auto-fix"></i> 逻辑拆解设置</span>
                      <i class="mdi mdi-close" @click.stop="showDisassembleParamsDialog = false; disassemblingGroupId = null"></i>
                    </div>
                    <div class="popover-body">
                      <label>请输入推广商品名称(选填)：</label>
                      <input 
                        type="text" 
                        v-model="disassembleParams.commodityName" 
                        placeholder="例如：高腰阔腿裤..."
                        class="commodity-input"
                        @keyup.enter="confirmDisassembleGroup"
                        autofocus
                      />
                      <p class="hint">填写后 AI 会根据商品特性更精准地进行维度对齐</p>
                    </div>
                    <div class="popover-footer">
                      <button class="cancel-btn" @click.stop="showDisassembleParamsDialog = false; disassemblingGroupId = null">取消</button>
                      <button class="confirm-btn" @click.stop="confirmDisassembleGroup" :disabled="isDisassembling[group.id]">
                        <i v-if="isDisassembling[group.id]" class="mdi mdi-loading mdi-spin"></i>
                        开始拆解
                      </button>
                    </div>
                  </div>
                </div>
                <button 
                  class="header-icon-btn ft-toggle-btn" 
                  :class="{ 'active-ft': fineTuningGroupId === group.id }"
                  @click.stop="toggleFineTuning(group)" 
                  title="进入精剪模式 (局部)"
                >
                  <i class="mdi mdi-scissors-cutting"></i>
                </button>
              </div>
              <button class="header-icon-btn delete-btn" @click.stop="removeGroup(gIndex)" title="删除组">×</button>
            </div>
            
            <div class="segments-list" v-if="expandedGroups[group.id] !== false" @click.stop="selectGroup(gIndex)">
              <draggable-group 
                :segments="group.segments || []" 
                :group-index="gIndex"
                :fine-tuning="fineTuningGroupId === group.id"
                :selected-word-locations="selectedWordLocations"
                :selected-word-location="selectedWordLocation"
                :selected-ids="selectedSegmentIds"
                :dup-info="poolDupInfo"
                @update:selected-ids="selectedSegmentIds = $event"
                :current-time="currentTime"
                :playing-video-id="previewSource ? (previewSource.video_id || previewSource.id) : null"
                @segment-click="handleSegmentClick"
                @dup-jump="handleDupJump"
                @word-click="handleWordClick"
                @word-mousedown="handleWordMouseDown"
                @word-mouseover="handleWordMouseOver"
                @split-segment="splitSegment"
                @merge-segment="mergeSegment"
              />

              <div v-if="!group.segments || group.segments.length === 0" class="empty-group-hint">
                暂无片段，请从左侧添加
              </div>
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <!-- AI Auto Clip Footer -->
    <!-- <div class="pool-footer">
      <button class="ai-btn" @click="openAiClipDialog" :disabled="totalSegments === 0">
        <i class="mdi mdi-robot-outline"></i> AI 一键剪辑
      </button>
    </div> -->

    <!-- AI KOL Selection Modal -->
    <div v-if="showAiDialog" class="export-overlay">
... (placeholder to find location)
      <div class="export-content" style="width: 500px; max-width: 90vw; max-height: 90vh; display: flex; flex-direction: column;">
        <div class="export-header">
          <h4>选择 AI 剪辑达人画像</h4>
          <button @click="showAiDialog = false" class="close-btn">×</button>
        </div>
        <div style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 15px;">
          <p style="font-size: 14px; color: #666;">请选择该视频的达人，以便应用专属的 AI 提示词和风格配置。</p>
          <select v-model="selectedKolId" @change="onKolSelected" class="kol-select">
            <option value="">-- 使用系统默认配置 --</option>
            <option v-for="kol in accessableKols" :key="kol.id" :value="kol.id">
              {{ kol.name }}
            </option>
          </select>

          <!-- Profile Editor -->
          <div v-if="selectedKolId && kolProfile" class="profile-editor" style="border: 1px solid #eee; padding: 15px; border-radius: 8px; background: #fafafa;">
             <h5 style="margin-top: 0; margin-bottom: 10px; color: #333;">专属画像配置 (可编辑)</h5>
             
             <div class="form-group" style="margin-bottom: 10px;">
                <label style="font-size: 12px; color: #666; display: block; margin-bottom: 4px;">专属词汇表 (逗号分隔，如：特有商品名、方言词)</label>
                <input v-model="profileForm.custom_vocabulary" type="text" placeholder="例如：某某品牌, 某某产品" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box;"/>
             </div>

             <div class="form-group" style="margin-bottom: 10px;">
                <label style="font-size: 12px; color: #666; display: block; margin-bottom: 4px;">开场钩子风格提示词</label>
                <textarea v-model="profileForm.hook_style_prompt" rows="2" placeholder="例如：喜欢用疑问句开头制造悬念" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box; resize: vertical;"></textarea>
             </div>

             <div class="form-group" style="margin-bottom: 10px;">
                <label style="font-size: 12px; color: #666; display: block; margin-bottom: 4px;">金句提取偏好</label>
                <textarea v-model="profileForm.golden_sentence_prompt" rows="2" placeholder="例如：侧重于提炼带数字或情绪强烈的结论" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box; resize: vertical;"></textarea>
             </div>

             <div class="form-group" style="margin-bottom: 10px;">
                <label style="font-size: 12px; color: #666; display: block; margin-bottom: 4px;">逻辑编排倾向</label>
                <textarea v-model="profileForm.logic_sequence_prompt" rows="2" placeholder="例如：痛点引入 -> 场景代入 -> 给出解决方案" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box; resize: vertical;"></textarea>
             </div>

             <div class="form-group" style="margin-bottom: 10px;">
                <label style="font-size: 12px; color: #666; display: block; margin-bottom: 4px;">冗余词剔除 (逗号分隔，如：口癖、语气词)</label>
                <input v-model="profileForm.redundancy_words" type="text" placeholder="例如：嗯, 啊, 就是说" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box;"/>
             </div>
             
             <div style="text-align: right; margin-top: 10px;">
                <span v-if="isSavingProfile" style="font-size: 12px; color: #999; margin-right: 10px;">保存中...</span>
                <button class="action-btn" style="padding: 4px 10px; font-size: 12px;" @click="saveProfile" :disabled="isSavingProfile">保存画像配置</button>
             </div>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 10px;">
            <button class="action-btn" @click="showAiDialog = false">取消</button>
            <button class="action-btn primary" @click="startAiClip" :disabled="isAiProcessing">
              {{ isAiProcessing ? '处理中...' : '开始剪辑' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Processing Overlay -->
    <div v-if="isAiProcessing" class="export-overlay" style="z-index: 2000;">
      <div class="export-content" style="width: 300px; text-align: center; padding: 40px 20px;">
        <i class="mdi mdi-loading mdi-spin" style="font-size: 40px; color: #2196f3; margin-bottom: 20px; display: inline-block;"></i>
        <h4 style="margin-bottom: 10px;">AI 智能剪辑中</h4>
        <p style="color: #666; font-size: 14px;">{{ aiProgressMessage }}</p>
      </div>
    </div>

    <!-- Export Options Modal -->
    <div v-if="showExportOptions" class="export-overlay">
      <div class="export-content" style="width: 400px; max-width: 90vw;">
        <div class="export-header">
          <h4>选择导出模式</h4>
          <button @click="showExportOptions = false" class="close-btn">×</button>
        </div>
        <div style="padding: 20px; display: flex; flex-direction: column; gap: 15px;">
           <button class="action-btn" style="height: auto; padding: 15px; text-align: left; display: flex; align-items: center; gap: 12px; border: 1px solid #ddd; background: white; color: #333;" @click="handleCopyAllText">
              <i class="mdi mdi-content-copy" style="font-size: 24px; color: #1976d2;"></i>
              <div>
                <div style="font-weight: bold; margin-bottom: 2px; font-size: 16px;">复制全部文本</div>
                <div style="font-size: 12px; color: #666; font-weight: normal;">复制剪辑池内所有片段的文案。</div>
              </div>
           </button>

           <button class="action-btn" style="height: auto; padding: 15px; text-align: left; display: block; border: 1px solid #ddd; background: white; color: #333;" @click="confirmExport(false)">
              <div style="font-weight: bold; margin-bottom: 5px; font-size: 16px;">按分组导出</div>
              <div style="font-size: 12px; color: #666; font-weight: normal;">保持当前分组结构，生成 {{ groups.length }} 个独立的视频文件。</div>
           </button>
           <div style="border: 1px solid #2196f3; border-radius: 4px; overflow: hidden; display: flex; flex-direction: column;">
             <button class="action-btn primary" style="height: auto; padding: 15px; text-align: left; display: block; border: none; border-radius: 0; width: 100%;" @click="confirmExport(true)">
                <div style="font-weight: bold; margin-bottom: 5px; font-size: 16px;">合并导出</div>
                <div style="font-size: 12px; opacity: 0.9; font-weight: normal;">将所有分组的片段按顺序合并为一个长视频文件。</div>
             </button>
             <div style="padding: 10px; background: #f5f9ff; border-top: 1px solid #bbd6ee;">
               <input v-model="exportRemark" type="text" placeholder="添加备注（可选）" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box; font-size: 13px;"/>
             </div>
             <div style="padding: 10px; background: #fff; border-top: 1px solid #eee; display: flex; flex-direction: column; gap: 8px;">
               <div style="display: flex; align-items: center; gap: 8px;">
                 <input type="checkbox" id="autoFlip" v-model="autoFlip" style="cursor: pointer;">
                 <label for="autoFlip" style="font-size: 14px; color: #333; cursor: pointer; user-select: none;">开启自动翻转 (提升去重，增加留存)</label>
               </div>
               <div style="display: flex; align-items: center; gap: 8px;">
                 <input type="checkbox" id="audioNorm" v-model="audioNorm" style="cursor: pointer;">
                 <label for="audioNorm" style="font-size: 14px; color: #333; cursor: pointer; user-select: none;">开启智能音频正规化 (响度统一，适合带货)</label>
               </div>
             </div>
           </div>>
        </div>
      </div>
    </div>

    <!-- Single Group Export Modal -->
    <div v-if="showSingleGroupExportDialog" class="export-overlay">
      <div class="export-content" style="width: 400px; max-width: 90vw;">
        <div class="export-header">
          <h4>分组操作: {{ groupToExport ? groupToExport.name : '' }}</h4>
          <button @click="showSingleGroupExportDialog = false" class="close-btn">×</button>
        </div>
        <div style="padding: 20px; display: flex; flex-direction: column; gap: 15px;">
           <button class="action-btn" style="height: auto; padding: 15px; text-align: left; display: flex; align-items: center; gap: 12px; border: 1px solid #ddd; background: white; color: #333;" @click="handleCopyGroupText">
              <i class="mdi mdi-content-copy" style="font-size: 24px; color: #1976d2;"></i>
              <div>
                <div style="font-weight: bold; margin-bottom: 2px; font-size: 16px;">复制文本</div>
                <div style="font-size: 12px; color: #666; font-weight: normal;">复制该分组内所有片段的文案。</div>
              </div>
           </button>
           
           <button class="action-btn primary" style="height: auto; padding: 15px; text-align: left; display: flex; align-items: center; gap: 12px; border: none;" @click="handleExportGroupVideo">
              <i class="mdi mdi-video-outline" style="font-size: 24px; color: white;"></i>
              <div>
                <div style="font-weight: bold; margin-bottom: 2px; font-size: 16px;">导出视频</div>
                <div style="font-size: 12px; opacity: 0.9; font-weight: normal;">提交该分组片段的剪辑导出任务。</div>
              </div>
           </button>

           <div style="border-top: 1px solid #eee; padding-top: 15px; display: flex; flex-direction: column; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <input type="checkbox" id="autoFlipSingle" v-model="autoFlip" style="cursor: pointer;">
                <label for="autoFlipSingle" style="font-size: 13px; color: #333; cursor: pointer;">开启自动翻转</label>
              </div>
              <div style="display: flex; align-items: center; gap: 8px;">
                <input type="checkbox" id="audioNormSingle" v-model="audioNorm" style="cursor: pointer;">
                <label for="audioNormSingle" style="font-size: 13px; color: #333; cursor: pointer;">开启智能音频正规化</label>
              </div>
           </div>
        </div>
      </div>
    </div>

    <!-- Export Progress Overlay -->
    <div v-if="showExportOverlay" class="export-overlay">
      <div class="export-content">
        <div class="export-header">
          <h4>导出进度</h4>
          <button @click="closeExportOverlay" class="close-btn">×</button>
        </div>
        <div class="task-list">
          <div v-for="task in exportTasks" :key="task.id" class="task-item">
            <span class="task-name">{{ task.name }}</span>
            <span class="task-status" :class="task.status">
              {{ task.message }}
            </span>
            <button 
              v-if="task.file_url" 
              class="download-btn" 
              @click="downloadFile(task.file_url)"
            >
              下载
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Help Modal -->
    <Teleport to="body">
      <div v-if="showHelp" class="help-overlay" @click.self="showHelp = false">
        <div class="help-modal">
          <div class="help-header">
            <h3>剪辑池功能指南</h3>
            <button class="close-btn" @click="showHelp = false">×</button>
          </div>
          <div class="help-content">
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-drag-vertical"></i></div>
              <div class="step-info">
                <h4>拖拽排序</h4>
                <p>按住拖拽手柄 <i class="mdi mdi-drag-vertical"></i> 可调整片段顺序。也支持在不同分组间拖拽。</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-folder-plus-outline"></i></div>
              <div class="step-info">
                <h4>分组管理</h4>
                <p>点击“新建组”可创建多个分组。点击组名可重命名。适合将素材归类（如：片头、正文、口播）。</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-scissors-cutting"></i></div>
              <div class="step-info">
                <h4>精剪模式</h4>
                <p>点击“精剪”按钮进入文本剪辑模式。在片段中选中不需要的文字，点击“删除”即可精准裁剪视频内容。</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-export"></i></div>
              <div class="step-info">
                <h4>导出</h4>
                <p><strong>按分组导出</strong>：生成多个视频文件（适合素材包）。<br><strong>合并导出</strong>：将所有片段合并为一个长视频。</p>
              </div>
            </div>
          </div>
          <div class="help-footer">
            <button class="primary-btn" @click="showHelp = false">知道了</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- AI Titles Dialog -->
    <Teleport to="body">
      <div v-if="showTitlesDialog" class="export-overlay" @click.self="showTitlesDialog = false">
        <div class="export-content" style="width: 550px; border-radius: 12px;">
          <div class="export-header">
            <h4><i class="mdi mdi-auto-fix" style="color: #7b1fa2; margin-right: 8px;"></i>AI 爆款标题建议</h4>
            <button @click="showTitlesDialog = false" class="close-btn">×</button>
          </div>
          <div style="padding: 10px 0;">
            <div v-if="aiTitlesData && aiTitlesData.reasoning" style="margin-bottom: 20px; padding: 12px; background: #f3e5f5; border-radius: 8px; font-size: 13px; color: #4a148c; line-height: 1.5;">
              <strong>编导思路：</strong> {{ aiTitlesData.reasoning }}
            </div>
            <div class="titles-list" style="display: flex; flex-direction: column; gap: 12px;">
              <div v-for="(title, idx) in (aiTitlesData ? aiTitlesData.titles : [])" :key="idx" 
                   style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: #f9f9f9; border: 1px solid #eee; border-radius: 8px; transition: all 0.2s;"
                   class="title-item-hover">
                <span style="font-size: 15px; color: #333; font-weight: 500; flex: 1; padding-right: 15px;">{{ title }}</span>
                <button @click="copyTextToClipboard(title)" 
                        style="padding: 6px 12px; background: white; border: 1px solid #ddd; border-radius: 4px; font-size: 12px; cursor: pointer; color: #666; display: flex; align-items: center; gap: 4px;"
                        class="copy-btn-hover">
                  <i class="mdi mdi-content-copy"></i> 复制
                </button>
              </div>
            </div>
          </div>
          <div style="margin-top: 20px; text-align: right;">
            <button class="primary-btn" @click="showTitlesDialog = false" style="background: #7b1fa2;">返回剪辑</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { create_batch_slices, get_accessable_kol_list, get_influencer_ai_profile, update_influencer_ai_profile } from '@/services/api'
import DraggableGroup from './DraggableGroup.vue'
import draggable from 'vuedraggable'
import axios from 'axios'
import { BASE_URL } from '@/config'
import { analyzeDuplicates } from '@/utils/segmentDuplicates'

export default {
  name: 'ClipPool',
  components: {
    DraggableGroup,
    draggable
  },
  data() {
    return {
      showHelp: false,
      isExporting: false,
      exportTasks: [],
      showExportOverlay: false,
      showExportOptions: false,
      pollingTimer: null,
      expandedGroups: {},
      fineTuningGroupId: null,
      prevGroupSegmentCounts: {},
      selectedWordLocation: null, // Cursor for playback
      selectedWordLocations: [], // Multi-selection for operations
      cachedRangeIndex: -1, // Cache for performance
      lastPoolTime: 0,
      isDraggingSelection: false,
      dragStartLocation: null,
      dragHasMoved: false,
      playMode: 'pool', // 'pool' or 'trace'
      showAiDialog: false,
      isAiProcessing: false,
      aiProgressMessage: '准备中...',
      selectedKolId: '',
      accessableKols: [],
      kolProfile: null,
      profileForm: {
        custom_vocabulary: '',
        hook_style_prompt: '',
        golden_sentence_prompt: '',
        logic_sequence_prompt: '',
        redundancy_words: ''
      },
      exportRemark: '',
      isSavingProfile: false,
      aiEventSource: null,
      highlightedGroupId: null,
      highlightedTrace: false,
      highlightedAiRefId: null,
      autoFlip: true,
      audioNorm: true,
      showSingleGroupExportDialog: false,
      groupToExport: null,
      selectedSegmentIds: [],
      isDisassembling: {}, // { groupId: boolean }
      disassemblingGroupId: null,
      showDisassembleParamsDialog: false,
      disassembleParams: {
        commodityName: ''
      },
      isOrchestrating: false,
      isGeneratingTitles: false,
      aiTitlesData: null,
      showTitlesDialog: false,
      showTitleParamsDialog: false,
      titleParams: {
        commodityName: ''
      },
      showOrchestrationParamsDialog: false,
      orchestrationParams: {
        commodityName: ''
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown)
    window.addEventListener('mouseup', this.handleGlobalMouseUp)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('mouseup', this.handleGlobalMouseUp)
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  },
  props: {
    currentTime: {
      type: Number,
      default: 0
    },
    isPlaying: {
      type: Boolean,
      default: false
    }
  },
  emits: ['play-segment', 'request-seek', 'request-pause'],
  computed: {
    ...mapState('multiClipper', ['groups', 'currentGroupIndex', 'previewSource', 'activePanel', 'sourceTraceSegments', 'aiReferenceGroups', 'disassemblyResults']),
    isFineTuning() {
      return !!this.fineTuningGroupId;
    },
    isAiAllowed() {
      const user = this.$store.getters.currentUser || JSON.parse(localStorage.getItem('user') || '{}');
      
      // 1. 白名单手机号 (用户要求的硬编码控制)
      if (user && user.phoneNumber === '17372776468') {
        return true;
      }
      
      // 2. 正常的权限控制逻辑 (从后端返回的 permissions 中判断)
      if (user && user.permissions) {
        // FeaturePage.AI_ASSISTANT 的值是 "AI 助手"
        return !!user.permissions["AI 助手"];
      }
      
      return false;
    },
    isAiProcessingAny() {
      return this.isOrchestrating || this.isGeneratingTitles;
    },
    canSplit() {
        if (this.selectedWordLocations.length !== 1) return false;
        return this.selectedWordLocations[0].wordIndex > 0;
    },
    canMerge() {
        if (this.selectedWordLocations.length !== 1) return false;
        const loc = this.selectedWordLocations[0];
        if (loc.wordIndex !== 0 || loc.segmentIndex === 0) return false;
        const group = this.groups[loc.groupIndex];
        if (!group || !group.segments) return false;
        const cur = group.segments[loc.segmentIndex];
        const prev = group.segments[loc.segmentIndex - 1];
        if (!cur || !prev) return false;
        const curId = cur.video_id || cur.transcription_id || cur.id;
        const prevId = prev.video_id || prev.transcription_id || prev.id;
        return curId === prevId;
    },
    totalSegments() {
      return this.groups.reduce((acc, group) => acc + (group.segments ? group.segments.length : 0), 0)
    },
    totalDuration() {
      return this.groups.reduce((acc, group) => acc + this.calculateGroupDuration(group), 0)
    },
    sourceTraceDuration() {
      return (this.sourceTraceSegments || []).reduce((acc, s) => acc + this.calculateSegmentDuration(s), 0)
    },
    activeSegments: {
      get() {
        if (this.currentGroupIndex === -1 || !this.groups[this.currentGroupIndex]) return []
        return this.groups[this.currentGroupIndex].segments || []
      },
      set(value) {
        if (this.currentGroupIndex !== -1) {
          this.$store.dispatch('multiClipper/updateGroupSegments', {
            groupIndex: this.currentGroupIndex,
            segments: value
          })
        }
      }
    },
    localGroups: {
      get() {
        return this.groups
      },
      set(value) {
        this.$store.dispatch('multiClipper/updateGroupsOrder', value)
      }
    },
    // 剪辑池重复素材分析（仅池内所有组的片段）
    dupAnalysis() {
      const allSegments = []
      this.groups.forEach(g => {
        if (g && g.segments) g.segments.forEach(s => allSegments.push(s))
      })
      return analyzeDuplicates(allSegments)
    },
    // 供 DraggableGroup 渲染的重复标记信息（附带配对片段详情用于 tooltip）
    poolDupInfo() {
      const { dupMap, segById } = this.dupAnalysis
      const info = {}
      for (const id of Object.keys(dupMap)) {
        const d = dupMap[id]
        info[id] = {
          ...d,
          pairs: d.pairIds.map(pid => {
            const p = segById[pid]
            if (!p) return null
            return {
              id: p.id,
              text: p.text || p.video_name || '片段',
              start: p.start_time,
              end: p.end_time
            }
          }).filter(Boolean)
        }
      }
      return info
    },
    // 顶部横幅统计数据
    dupStats() {
      const dupMap = this.dupAnalysis.dupMap
      const ids = Object.keys(dupMap)
      if (ids.length === 0) return { groupCount: 0, segmentCount: 0 }
      const groupCount = new Set(ids.map(id => dupMap[id].groupId)).size
      return { groupCount, segmentCount: ids.length }
    },
    playbackQueue() {
        const ranges = []
        
        if (this.playMode === 'trace') {
            if (!this.sourceTraceSegments) return ranges
            this.sourceTraceSegments.forEach((s, sIdx) => {
                const sVideoId = s.video_id || s.id
                const gIdx = -999 // Special group index for trace
                
                if (s.words && s.words.length > 0) {
                     s.words.forEach((w, wIdx) => {
                         if (!w.deleted) {
                             ranges.push({
                                start: Number(w.start_time),
                                end: Number(w.end_time),
                                videoId: sVideoId,
                                segment: s,
                                groupIndex: gIdx,
                                segmentIndex: sIdx,
                                wordIndex: wIdx
                            })
                         }
                     })
                } else {
                    ranges.push({
                        start: Number(s.start_time),
                        end: Number(s.end_time),
                        videoId: sVideoId,
                        segment: s,
                        groupIndex: gIdx,
                        segmentIndex: sIdx
                    })
                }
            })
            return ranges
        }

        if (!this.groups) return ranges
        
        this.groups.forEach((g, gIdx) => {
            if (!g.segments) return
            g.segments.forEach((s, sIdx) => {
                const sVideoId = s.video_id || s.id 
                // Use words if available (Fine-tuning granularity is always respected if data exists)
                if (s.words && s.words.length > 0) {
                     s.words.forEach((w, wIdx) => {
                         if (!w.deleted) {
                             ranges.push({
                                start: Number(w.start_time),
                                end: Number(w.end_time),
                                videoId: sVideoId,
                                segment: s,
                                groupIndex: gIdx,
                                segmentIndex: sIdx,
                                wordIndex: wIdx
                            })
                         }
                     })
                } else {
                    ranges.push({
                        start: Number(s.start_time),
                        end: Number(s.end_time),
                        videoId: sVideoId,
                        segment: s,
                        groupIndex: gIdx,
                        segmentIndex: sIdx
                    })
                }
            })
        })
        return ranges
    }
  },
  methods: {
    ...mapActions('multiClipper', ['createGroup', 'deleteGroup', 'deleteSegment', 'updateGroupName', 'updateSegment', 'setActivePanel', 'updateGroupsOrder', 'setSourceTraceSegments', 'setHighlightRanges', 'setGroups', 'updateGroupSegments', 'addAiReferenceGroup', 'removeAiReferenceGroup', 'setDisassemblyResult', 'clearAllGroups']),
    ...mapMutations('multiClipper', ['SET_CURRENT_GROUP_INDEX']),
    
    async openAiClipDialog() {
      this.showAiDialog = true;
      if (this.accessableKols.length === 0) {
        try {
          const res = await get_accessable_kol_list();
          let data = res;
          if (res.data) {
            data = res.data;
            if (data.data) {
              data = data.data;
            }
          }
          this.accessableKols = data || [];
        } catch (e) {
          console.error("Failed to fetch KOL list", e);
        }
      }
    },

    async onKolSelected() {
      if (!this.selectedKolId) {
        this.kolProfile = null;
        return;
      }
      try {
        const res = await get_influencer_ai_profile(this.selectedKolId);
        let data = res;
        if (res.data) {
          data = res.data;
          if (data.data) {
            data = data.data;
          }
        }
        this.kolProfile = data || {};
        
        // Sync to form
        this.profileForm = {
          custom_vocabulary: (this.kolProfile.custom_vocabulary || []).join(', '),
          hook_style_prompt: this.kolProfile.hook_style_prompt || '',
          golden_sentence_prompt: this.kolProfile.golden_sentence_prompt || '',
          logic_sequence_prompt: this.kolProfile.logic_sequence_prompt || '',
          redundancy_words: (this.kolProfile.redundancy_words || []).join(', ')
        };
      } catch (e) {
        console.error("Failed to fetch AI profile", e);
      }
    },

    async saveProfile() {
      if (!this.selectedKolId) return;
      this.isSavingProfile = true;
      try {
        const payload = {
          kol_id: this.selectedKolId,
          custom_vocabulary: this.profileForm.custom_vocabulary.split(',').map(s => s.trim()).filter(s => s),
          hook_style_prompt: this.profileForm.hook_style_prompt,
          golden_sentence_prompt: this.profileForm.golden_sentence_prompt,
          logic_sequence_prompt: this.profileForm.logic_sequence_prompt,
          redundancy_words: this.profileForm.redundancy_words.split(',').map(s => s.trim()).filter(s => s)
        };
        
        const res = await update_influencer_ai_profile(payload);
        this.$store.commit('snackbar/show', {
          text: '画像配置已保存',
          color: 'success'
        });
        
        // Update local profile
        let data = res;
        if (res.data) {
          data = res.data;
          if (data.data) {
            data = data.data;
          }
        }
        if (data) {
           this.kolProfile = data;
        }
      } catch (e) {
        console.error("Failed to save AI profile", e);
        this.$store.commit('snackbar/show', {
          text: '保存失败: ' + e.message,
          color: 'error'
        });
      } finally {
        this.isSavingProfile = false;
      }
    },
    
    startAiClip() {
      if (this.totalSegments === 0) return;
      this.showAiDialog = false;
      this.isAiProcessing = true;
      this.aiProgressMessage = '准备上传素材...';
      
      const payload = {
        kol_id: this.selectedKolId || "",
        segments: []
      };
      
      this.groups.forEach(g => {
        if (!g.segments) return;
        g.segments.forEach(s => {
          payload.segments.push({
            video_id: s.video_id || s.id,
            transcription_id: s.transcription_id || s.video_id || s.id,
            text: s.text || "",
            video_url: s.video_url || "",
            start_time: Number(s.start_time),
            end_time: Number(s.end_time),
            // 优化：不再发送巨大的 words 数组。后端会自动根据 transcription_id 从数据库补全。
            words: []
          });
        });
      });
      
      // Use SSE or mock API
      this.connectAiClipSSE(payload);
    },
    
    async connectAiClipSSE(payload) {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token');
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(BASE_URL + 'auto_clip/generate', {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(payload)
        });
        
        if (response.status === 401) {
          this.$store.dispatch('logout');
          this.$router.replace({
            path: "/login",
            query: { redirect: this.$router.currentRoute.fullPath }
          });
          throw new Error('登录已过期，请重新登录');
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            const resData = await response.json();
            if (resData.code && resData.code !== 200) {
                throw new Error(resData.msg || '请求失败');
            }
            // 如果碰巧是200的json，可以直接结束
            this.isAiProcessing = false;
            return;
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        
        let buffer = '';
        
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const dataStr = line.substring(6);
              try {
                const data = JSON.parse(dataStr);
                if (data.status === 'running') {
                  this.aiProgressMessage = `第 ${data.phase} 阶段: ${data.message || '处理中...'}`;
                } else if (data.status === 'success') {
                  this.isAiProcessing = false;
                  this.$store.commit('snackbar/show', {
                    text: 'AI 剪辑完成',
                    color: 'success'
                  });
                  // Replace current pool with returned groups
                  if (data.groups) {
                     this.updateGroupsOrder(data.groups);
                  }
                  return;
                } else if (data.status === 'error') {
                  throw new Error(data.message || 'AI processing error');
                }
              } catch (err) {
                console.error('Error parsing SSE data', err, dataStr);
              }
            }
          }
        }
      } catch (e) {
        console.error(e);
        this.isAiProcessing = false;
        this.$store.commit('snackbar/show', {
          text: 'AI 剪辑失败: ' + e.message,
          color: 'error'
        });
      }
    },

    closeSourceTrace() {
        if (this.highlightedTrace) {
            this.highlightedTrace = false
            this.setHighlightRanges([])
        }
        this.setSourceTraceSegments([])
    },

    removeAiReferenceGroupWrap(ari) {
        const group = this.aiReferenceGroups[ari]
        if (group && this.highlightedAiRefId === group.id) {
            this.highlightedAiRefId = null
            this.setHighlightRanges([])
        }
        this.removeAiReferenceGroup(ari)
    },

    toggleFineTuning(group) {
      if (this.fineTuningGroupId === group.id) {
        this.fineTuningGroupId = null;
      } else {
        this.fineTuningGroupId = group.id;
        // 确保进入精剪模式时组是展开的
        this.expandedGroups[group.id] = true;
        // 清空之前的文字选择，避免跨组操作逻辑混乱
        this.selectedWordLocations = [];
      }
    },

    async disassembleGroup(gIndex) {
      if (this.isOrchestrating || this.isGeneratingTitles) return;
      const group = this.groups[gIndex];
      if (!group || !group.segments || group.segments.length === 0) return;
      
      this.disassemblingGroupId = group.id;
      this.disassembleParams.commodityName = this.detectPreferredCommodity();
      this.showDisassembleParamsDialog = true;
    },

    async confirmDisassembleGroup() {
      const gIndex = this.groups.findIndex(g => g.id === this.disassemblingGroupId);
      if (gIndex === -1) return;
      
      const group = this.groups[gIndex];
      const commodityName = this.disassembleParams.commodityName;
      
      this.showDisassembleParamsDialog = false;
      this.isDisassembling[group.id] = true;
      try {
        const result = await axios.post(BASE_URL + 'api/group/ai-disassemble', {
          segments: group.segments.map(seg => ({
            text: seg.text,
            video_id: seg.video_id || seg.id,
            transcription_id: seg.transcription_id || seg.video_id || seg.id,
            start_time: seg.start_time || seg.start,
            end_time: seg.end_time || seg.end,
            // 优化：不再发送巨大的 words 数组。后端会自动根据 transcription_id 从数据库补全打点信息。
            // 这样可以大幅减小请求体积，避免长文本时触发网关（OpenResty）的 404/413 错误。
            words: []
          })),
          commodity_name: commodityName
        });
        
        // 由于项目配置了全局 axios 拦截器，code=200 时会直接返回 data 部分
        if (result) {
          // 保存结果到全局 store，避免组件销毁后丢失
          this.setDisassemblyResult({
            groupId: group.id,
            result: result
          });
          
          // 将其转化为“参考源”，移出普通剪辑组
          this.addAiReferenceGroup(JSON.parse(JSON.stringify(group)));
          this.removeGroup(gIndex, true);
          
          this.$store.commit('snackbar/show', {
            text: 'AI 拆解完成，已移至参考源',
            color: 'success'
          });
        } else {
          throw new Error('未返回有效数据');
        }
      } catch (e) {
        console.error('AI disassembly error:', e);
        const msg = (e && e.msg) || e.message || '网络或业务错误';
        this.$store.commit('snackbar/show', {
          text: 'AI 拆解失败: ' + msg,
          color: 'error'
        });
      } finally {
        this.isDisassembling[group.id] = false;
        this.disassemblingGroupId = null;
      }
    },

    detectPreferredCommodity() {
      const counts = {};
      this.groups.forEach(g => {
        if (g.segments) {
          g.segments.forEach(s => {
            if (s.commodity_name) {
              counts[s.commodity_name] = (counts[s.commodity_name] || 0) + 1;
            }
          });
        }
      });
      
      let maxCount = 0;
      let winner = '';
      for (const name in counts) {
        if (counts[name] > maxCount) {
          maxCount = counts[name];
          winner = name;
        }
      }
      return winner;
    },

    async aiOrchestrate() {
      if (this.totalSegments === 0) return;
      this.showTitleParamsDialog = false; // Close other dialog
      
      // 预填众数商品名
      this.orchestrationParams.commodityName = this.detectPreferredCommodity();
      
      this.showOrchestrationParamsDialog = true;
    },

    async confirmAiOrchestrate() {
      this.showOrchestrationParamsDialog = false;
      this.isOrchestrating = true;
      try {
        // Collect ALL segments from all groups to re-orchestrate
        const allSegments = [];
        this.groups.forEach(g => {
          if (g.segments) {
            g.segments.forEach(s => {
              allSegments.push({
                ...s,
                id: s.id || `seg_${Math.random().toString(36).substr(2, 9)}`
              });
            });
          }
        });

        const result = await axios.post(BASE_URL + 'api/pool/ai-orchestrate', {
          segments: allSegments.map(seg => ({
            id: seg.id,
            text: seg.text,
            video_id: seg.video_id,
            transcription_id: seg.transcription_id,
            start_time: seg.start_time,
            end_time: seg.end_time,
            // 优化：编排接口不需要 words 数据，置空以减小传输体积
            words: []
          })),
          commodity_name: this.orchestrationParams.commodityName
        });

        if (result && result.orchestration) {
          const newGroups = [];
          const segmentMap = new Map();
          allSegments.forEach(s => segmentMap.set(s.id, s));

          result.orchestration.forEach((orcGroup, index) => {
            const orchestratedSegments = [];
            orcGroup.segment_ids.forEach(sid => {
              if (segmentMap.has(sid)) {
                orchestratedSegments.push(JSON.parse(JSON.stringify(segmentMap.get(sid))));
              }
            });

            if (orchestratedSegments.length > 0) {
              newGroups.push({
                id: `ai_group_${Date.now()}_${index}`,
                name: orcGroup.group_name,
                segments: orchestratedSegments
              });
            }
          });

          if (newGroups.length > 0) {
            this.$store.dispatch('multiClipper/updateGroupsOrder', newGroups);
            this.$store.commit('snackbar/show', {
              text: 'AI 编排完成',
              color: 'success'
            });
          } else {
            throw new Error('AI 编排未返回有效分组');
          }
        } else {
          throw new Error('未返回有效的编排建议');
        }
      } catch (e) {
        console.error('AI orchestration error:', e);
        const msg = (e && e.data && e.data.msg) || e.message || '网络或业务错误';
        this.$store.commit('snackbar/show', {
          text: 'AI 编排失败: ' + msg,
          color: 'error'
        });
      } finally {
        this.isOrchestrating = false;
      }
    },

    async aiGenerateTitles() {
      if (this.totalSegments === 0) return;
      this.showOrchestrationParamsDialog = false; // Close other dialog
      
      // 预填众数商品名
      this.titleParams.commodityName = this.detectPreferredCommodity();
      
      this.showTitleParamsDialog = true;
    },

    async confirmGenerateTitles() {
      this.showTitleParamsDialog = false;
      this.isGeneratingTitles = true;
      try {
        const allSegments = [];
        this.groups.forEach(g => {
          if (g.segments) {
            g.segments.forEach(s => {
              allSegments.push(s);
            });
          }
        });

        const result = await axios.post(BASE_URL + 'api/pool/ai-generate-titles', {
          segments: allSegments.map(seg => ({
            text: seg.text || ""
          })),
          commodity_name: this.titleParams.commodityName
        });

        if (result && result.titles) {
          this.aiTitlesData = result;
          this.showTitlesDialog = true;
        } else {
          throw new Error('未返回有效标题');
        }
      } catch (e) {
        console.error('AI title gen error:', e);
        const msg = (e && e.data && e.data.msg) || e.message || '生成失败';
        this.$store.commit('snackbar/show', {
          text: 'AI 生成标题失败: ' + msg,
          color: 'error'
        });
      } finally {
        this.isGeneratingTitles = false;
      }
    },

    jumpToQuote(quote, group) {
      if (!quote || quote.start_time === undefined || quote.start_time < 0) {
        this.$store.commit('snackbar/show', { text: '该片段未匹配到时间点', color: 'info' });
        return;
      }
      
      // 优先从当前组中寻找匹配的片段，以获取完整的 metadata (如 video_url, words 等)
      let segment = null;
      if (group && group.segments) {
        segment = group.segments.find(s => {
          const sid = s.video_id || s.id;
          const qid = quote.video_id;
          return sid && qid && String(sid) === String(qid);
        });
      }
      
      // 如果没找到，则使用 quote 本身构造
      const jumpTarget = segment ? { ...segment } : { ...quote };
      
      // 覆盖跳转具体时间点
      jumpTarget.start_time = quote.start_time;
      jumpTarget.end_time = quote.end_time;
      jumpTarget.text = quote.text;
      jumpTarget.autoPlay = true;
      jumpTarget.targetPanel = 'source';

      // 设置全局选择范围，让 SourceViewer 能够高亮并滚动到对应文字
      this.$store.dispatch('multiClipper/setExternalSelectionRange', {
          start_time: quote.start_time,
          end_time: quote.end_time,
          video_id: jumpTarget.video_id || jumpTarget.id,
          transcription_id: jumpTarget.transcription_id || jumpTarget.video_id || jumpTarget.id
      })

      // 触发跳转逻辑
      this.$emit('play-segment', jumpTarget);
    },
    
    cloneSegment(original) {
        return {
            ...original,
            id: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 5)
        }
    },

    handleContainerClick(event) {
        // Avoid triggering when clicking buttons or inputs
        if (event.target.tagName === 'BUTTON' || event.target.tagName === 'INPUT' || event.target.closest('button')) {
            return
        }
        
        // Clear selection if clicking background
        if (!event.ctrlKey && !event.shiftKey) {
            this.selectedWordLocations = []
        }
        
        if (this.activePanel !== 'pool') {
            this.setActivePanel('pool')
            // Resume from last position
            if (this.lastPoolTime > 0) {
                this.$emit('request-seek', this.lastPoolTime)
            } else if (this.playbackQueue.length > 0) {
                // If no history, start from beginning of first segment
                this.$emit('request-seek', this.playbackQueue[0].start / 1000)
            }
        }
    },

    // 点击重复标记后定位到配对片段：展开所在组并滚动到该片段，短暂高亮
    handleDupJump(segmentId) {
        if (!segmentId) return
        for (let gIndex = 0; gIndex < this.groups.length; gIndex++) {
            const group = this.groups[gIndex]
            const segs = (group && group.segments) || []
            if (segs.some(s => s.id === segmentId)) {
                if (group.id) this.expandedGroups[group.id] = true
                this.$nextTick(() => {
                    const escaped = String(segmentId).replace(/"/g, '\\"')
                    const el = this.$el.querySelector(`.segment-item[data-segment-id="${escaped}"]`)
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        el.classList.add('dup-jump-flash')
                        setTimeout(() => el.classList.remove('dup-jump-flash'), 1600)
                    }
                })
                return
            }
        }
    },

    handleSequencePlayback(timeSeconds) {
        // Allow playback update if active panel is pool OR if we are in fine tuning mode
        if (this.activePanel !== 'pool' && !this.isFineTuning) return
        if (!this.playbackQueue || this.playbackQueue.length === 0) return
        if (!this.previewSource) return

        const timeMs = timeSeconds * 1000
        const currentVideoId = this.previewSource.video_id || this.previewSource.id
        const queue = this.playbackQueue
        
        let currentRange = null
        let currentIndex = -1

        // 1. Try cache
        if (this.cachedRangeIndex >= 0 && this.cachedRangeIndex < queue.length) {
            const r = queue[this.cachedRangeIndex]
            if (r.videoId === currentVideoId && timeMs >= r.start - 100 && timeMs <= r.end + 50) {
                currentRange = r
                currentIndex = this.cachedRangeIndex
            }
        }

        // 2. Try next item (sequential playback optimization)
        if (!currentRange && this.cachedRangeIndex >= 0 && this.cachedRangeIndex + 1 < queue.length) {
            const next = queue[this.cachedRangeIndex + 1]
            if (next.videoId === currentVideoId && timeMs >= next.start - 100 && timeMs <= next.end + 50) {
                currentRange = next
                currentIndex = this.cachedRangeIndex + 1
            }
        }

        // 3. Full search (fallback)
        if (!currentRange) {
             // Search Strategy:
             // A. Try to stay in the current segment (if known)
             if (this.selectedWordLocation) {
                 const { groupIndex, segmentIndex } = this.selectedWordLocation
                 const matchInContextIndex = queue.findIndex(r => 
                     r.videoId === currentVideoId &&
                     r.groupIndex === groupIndex && 
                     r.segmentIndex === segmentIndex &&
                     timeMs >= r.start - 100 && timeMs <= r.end + 50
                 )
                 
                 if (matchInContextIndex !== -1 && matchInContextIndex >= this.cachedRangeIndex) {
                     currentIndex = matchInContextIndex
                 }
             }

             // B. Search forward from cache first
             if (currentIndex === -1) {
                 const searchStart = Math.max(0, this.cachedRangeIndex)
                 for (let i = searchStart; i < queue.length; i++) {
                     const r = queue[i]
                     if (r.videoId === currentVideoId && timeMs >= r.start - 100 && timeMs <= r.end + 50) {
                         currentIndex = i
                         break
                     }
                 }
                 // If not found forward, search from beginning as a last resort
                 if (currentIndex === -1) {
                     currentIndex = queue.findIndex(r => r.videoId === currentVideoId && timeMs >= r.start - 100 && timeMs <= r.end + 50)
                 }
             }
             
             if (currentIndex !== -1) {
                 currentRange = queue[currentIndex]
             }
        }
        
        // Update cache
        if (currentIndex !== -1) {
            this.cachedRangeIndex = currentIndex
        }

        // Logic for seeking/switching
        if (currentRange) {
            // Update cursor in fine-tuning mode (UI focus)
            if (currentRange.wordIndex !== undefined) {
                 if (!this.selectedWordLocation || 
                     this.selectedWordLocation.groupIndex !== currentRange.groupIndex ||
                     this.selectedWordLocation.segmentIndex !== currentRange.segmentIndex ||
                     this.selectedWordLocation.wordIndex !== currentRange.wordIndex) {
                     
                     this.selectedWordLocation = {
                         groupIndex: currentRange.groupIndex,
                         segmentIndex: currentRange.segmentIndex,
                         wordIndex: currentRange.wordIndex
                     }
                     // 同步更新选择列表，确保 UI 上的“光标”（蓝色框）跟随播放
                     if (this.selectedWordLocations.length <= 1) {
                         this.selectedWordLocations = [this.selectedWordLocation]
                     }
                 }
            }

            if (this.activePanel === 'pool') {
                const remaining = currentRange.end - timeMs
                if (remaining < 80) {
                    this.jumpToNext(currentIndex)
                }
            }
        } else {
            // ... Gap handle logic omitted for brevity in trace but kept in file ...
            if (this.activePanel === 'pool') {
                let organicAdvance = false
                if (this.cachedRangeIndex !== -1) {
                    const lastRange = queue[this.cachedRangeIndex]
                    if (lastRange && lastRange.videoId === currentVideoId && timeMs >= lastRange.end && timeMs < lastRange.end + 500) {
                        organicAdvance = true
                    }
                }

                if (organicAdvance) {
                    this.jumpToNext(this.cachedRangeIndex)
                } else {
                    const searchStart = Math.max(0, this.cachedRangeIndex)
                    let nextIndex = -1
                    for (let i = searchStart; i < queue.length; i++) {
                        if (queue[i].videoId === currentVideoId && queue[i].start > timeMs) {
                            nextIndex = i
                            break
                        }
                    }
                    if (nextIndex === -1) {
                        nextIndex = queue.findIndex(r => r.videoId === currentVideoId && r.start > timeMs)
                    }

                    if (nextIndex !== -1) {
                        const nextRange = queue[nextIndex]
                        this.executeJump(nextRange)
                        this.cachedRangeIndex = nextIndex
                    }
                }
            }
        }
    },

    jumpToNext(currentIndex) {
        // If we are at the end of the sequence, stop playback
        if (currentIndex + 1 >= this.playbackQueue.length) {
            this.$emit('request-pause')
            return
        }
        
        const currentRange = this.playbackQueue[currentIndex]
        const nextRange = this.playbackQueue[currentIndex + 1]
        
        // 如果跨段（不同的组或不同的片段），且处于溯源模式，则停止播放，不自动播下一段
        if (this.playMode === 'trace' && (currentRange.groupIndex !== nextRange.groupIndex || currentRange.segmentIndex !== nextRange.segmentIndex)) {
            this.$emit('request-pause')
            return
        }
        
        // Always execute jump if we change segments manually or organically
        // The natural playback check relies on strict word indices which might fail after splitting
        const gap = nextRange.start - currentRange.end
        
        let isContinuousWord = false;
        if (currentRange.wordIndex !== undefined && nextRange.wordIndex !== undefined &&
            currentRange.segment && currentRange.segment.words && 
            nextRange.segment && nextRange.segment.words) {
            const currentWord = currentRange.segment.words[currentRange.wordIndex];
            const nextWord = nextRange.segment.words[nextRange.wordIndex];
            if (currentWord && nextWord && currentWord.index_w !== undefined && nextWord.index_w !== undefined) {
                // Only consider it continuous if it's exactly the next word AND they belong to the same original segment sequence
                if (currentWord.index_w + 1 === nextWord.index_w && currentRange.segment.id === nextRange.segment.id) {
                    isContinuousWord = true;
                }
            }
        }
        
        if (nextRange.videoId === currentRange.videoId && (isContinuousWord || (gap < 100 && gap > -100 && currentRange.segment.id === nextRange.segment.id))) {
            // Natural playback continues ONLY if they are truly the same segment piece
            this.cachedRangeIndex = currentIndex + 1
            return
        }
        
        this.executeJump(nextRange)
        this.cachedRangeIndex = currentIndex + 1
    },

    executeJump(range) {
        const currentVideoId = this.previewSource ? (this.previewSource.video_id || this.previewSource.id) : null
        
        if (range.videoId === currentVideoId) {
             this.$emit('request-seek', range.start / 1000)
        } else {
             // Different video
             const autoPlay = true
           this.$emit('play-segment', { ...range.segment, autoPlay, targetPanel: 'pool' })
        }
    },

    createNewGroup() {
      this.createGroup()
    },

    clearPool() {
      if (confirm('确定要清空整个剪辑池吗？此操作不可撤销。')) {
        if (this.highlightedGroupId) {
          this.highlightedGroupId = null
          this.setHighlightRanges([])
        }
        this.clearAllGroups()
      }
    },

    handleGroupNameChange(index, newName) {
      this.updateGroupName({ groupIndex: index, name: newName })
    },

    handleGroupNameInput(index, event) {
      this.handleGroupNameChange(index, event.target.value);
      this.autoResizeTextarea(event);
    },

    autoResizeTextarea(event) {
      const el = event.target;
      el.style.height = 'auto';
      el.style.height = (el.scrollHeight) + 'px';
    },

    toggleHighlight(group) {
      if (this.highlightedGroupId === group.id) {
        this.highlightedGroupId = null;
        this.setHighlightRanges([]);
      } else {
        this.highlightedGroupId = group.id;
        this.highlightedTrace = false;
        this.highlightedAiRefId = null;
        if (group.segments && group.segments.length > 0) {
          const ranges = group.segments.map(s => ({
              start_time: s.start_time,
              end_time: s.end_time,
              video_id: s.video_id || s.id
          }))
          this.setHighlightRanges(ranges)
        } else {
          this.setHighlightRanges([])
        }
      }
    },

    // 热点视频溯源组高亮：按源视频 video_id + start_time/end_time 在源视频文稿中高亮
    toggleTraceHighlight() {
      if (this.highlightedTrace) {
        this.highlightedTrace = false;
        this.setHighlightRanges([]);
      } else {
        this.highlightedTrace = true;
        this.highlightedGroupId = null;
        this.highlightedAiRefId = null;
        if (this.sourceTraceSegments && this.sourceTraceSegments.length > 0) {
          const ranges = this.sourceTraceSegments.map(s => ({
              start_time: s.start_time,
              end_time: s.end_time,
              video_id: s.video_id || s.id,
              color: '#FF8A65'
          }))
          this.setHighlightRanges(ranges)
        } else {
          this.setHighlightRanges([])
        }
      }
    },

    // AI 参考源组高亮：按拆解报告 quotes 的 video_id + start_time/end_time 高亮
    toggleAiRefHighlight(group) {
      if (this.highlightedAiRefId === group.id) {
        this.highlightedAiRefId = null;
        this.setHighlightRanges([]);
      } else {
        this.highlightedAiRefId = group.id;
        this.highlightedGroupId = null;
        this.highlightedTrace = false;
        const result = this.disassemblyResults[group.id]
        const quotes = result ? Object.values(result).flat() : []
        const ranges = quotes
          .filter(q => q.start_time !== undefined && q.start_time > 0 && q.end_time !== undefined)
          .map(q => ({
              start_time: q.start_time,
              end_time: q.end_time,
              video_id: q.video_id,
              color: '#CE93D8'
          }))
        this.setHighlightRanges(ranges)
      }
    },

    toggleGroup(index, group) {
      this.selectGroup(index)
      const current = this.expandedGroups[group.id] !== false
      this.expandedGroups[group.id] = !current
    },
    
    selectGroup(index) {
      if (this.currentGroupIndex !== index) {
        this.SET_CURRENT_GROUP_INDEX(index)
      }
    },
    
    removeGroup(index, skipConfirm = false) {
      if (skipConfirm || confirm('确定要删除这个组吗？')) {
        const group = this.groups[index]
        if (group && this.highlightedGroupId === group.id) {
            this.highlightedGroupId = null
            this.setHighlightRanges([])
        }
        this.deleteGroup(index)
      }
    },
    
    removeSegment(gIndex, sIndex) {
      this.deleteSegment({ groupIndex: gIndex, segmentIndex: sIndex })
    },
    
    checkSourceSwitch(segment) {
      // Logic moved to autoPlay handling, no need to pause here.
      // We rely on VideoPlayer suppression logic for stability.
    },

    handleSegmentClick(segment) {
      // Check if this segment is from source trace or AI reference
      const isSourceTrace = this.sourceTraceSegments && this.sourceTraceSegments.some(s => s.id === segment.id)
      const isAiRef = this.aiReferenceGroups && this.aiReferenceGroups.some(g => g.segments && g.segments.some(s => s.id === segment.id))

      if (isAiRef) {
          // For AI reference segments, switch to source view to play as material
          this.$emit('play-segment', { ...segment, autoPlay: true, targetPanel: 'source' })
          return
      }

      this.setActivePanel('pool')

      // Check if this segment is from source trace and set selection range
      if (isSourceTrace) {
          this.playMode = 'trace'
          this.$store.dispatch('multiClipper/setExternalSelectionRange', {
              start_time: segment.start_time,
              end_time: segment.end_time,
              video_id: segment.video_id || segment.id,
              transcription_id: segment.transcription_id || segment.video_id || segment.id
          })
      } else {
          this.playMode = 'pool'
          // Clear external selection if clicking normal segments
          this.$store.dispatch('multiClipper/setExternalSelectionRange', null)
      }
      
      // Update cache to point to this segment to prevent cursor jumping to duplicates
      // Note: playbackQueue will be re-calculated based on playMode, so we wait for next tick or assume reactivity works
      this.$nextTick(() => {
          const qIdx = this.playbackQueue.findIndex(item => item.segment.id === segment.id) // Use ID for safer match
          if (qIdx !== -1) {
              this.cachedRangeIndex = qIdx
          }
      })

      // Pass autoPlay intent: if currently playing, we want to continue playing after switch/seek
      const autoPlay = this.isPlaying
      this.$emit('play-segment', { ...segment, autoPlay, targetPanel: 'pool' })
    },

    getAllWordsFlat() {
        const flat = []
        if (!this.groups) return flat
        this.groups.forEach((g, gIdx) => {
            if (!g.segments) return
            g.segments.forEach((s, sIdx) => {
                if (s.words) {
                    s.words.forEach((w, wIdx) => {
                        flat.push({ groupIndex: gIdx, segmentIndex: sIdx, wordIndex: wIdx })
                    })
                }
            })
        })
        return flat
    },

    handleWordMouseDown({ event, groupIndex, segmentIndex, wordIndex }) {
        if (!this.isFineTuning) return
        this.isDraggingSelection = true
        this.dragStartLocation = { groupIndex, segmentIndex, wordIndex }
        this.dragHasMoved = false
        
        // Start new selection if no modifiers
        if (!event.ctrlKey && !event.shiftKey) {
             this.selectedWordLocations = [{ groupIndex, segmentIndex, wordIndex }]
             this.selectedWordLocation = { groupIndex, segmentIndex, wordIndex }
        }
    },

    handleWordMouseOver({ groupIndex, segmentIndex, wordIndex }) {
        if (!this.isFineTuning || !this.isDraggingSelection || !this.dragStartLocation) return
        
        const currentLoc = { groupIndex, segmentIndex, wordIndex }
        
        // Check if we actually moved to a different word
        if (currentLoc.groupIndex === this.dragStartLocation.groupIndex &&
            currentLoc.segmentIndex === this.dragStartLocation.segmentIndex &&
            currentLoc.wordIndex === this.dragStartLocation.wordIndex) {
            return
        }

        this.dragHasMoved = true
        this.updateSelectionRange(this.dragStartLocation, currentLoc)
    },

    handleGlobalMouseUp() {
        this.isDraggingSelection = false
        this.dragStartLocation = null
        this.dragHasMoved = false
    },

    // Clone function for dragging from Source Trace to Clip Pool
    cloneTraceSegment(original) {
        // Ensure we create a fresh copy with a new ID
        // Note: The store usually expects an ID, or generates one.
        // But since we are bypassing the store's 'addSegment' action during drag (vuedraggable handles the array update),
        // we should generate a unique ID here to avoid conflicts.
        const newId = `manual-trace-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        
        // console.log('Cloning Trace Segment:', JSON.stringify(original, null, 2))
        
        return {
            ...original,
            id: newId,
            // Ensure mandatory fields are present and correct type
            start_time: Number(original.start_time),
            end_time: Number(original.end_time),
            text: original.text || original.origin_text || '',
            // Ensure words are preserved
            words: original.words ? JSON.parse(JSON.stringify(original.words)) : []
        }
    },

    batchDeleteSegments() {
      if (this.selectedSegmentIds.length === 0) return
      if (!confirm(`确定删除选中的 ${this.selectedSegmentIds.length} 个片段吗？`)) return
      
      this.groups.forEach((group, gIndex) => {
        const remaining = (group.segments || []).filter(s => !this.selectedSegmentIds.includes(s.id))
        if (remaining.length !== (group.segments || []).length) {
          this.updateGroupSegments({ groupIndex: gIndex, segments: remaining })
        }
      })
      this.selectedSegmentIds = []
    },

    batchMoveToGroup(targetGroupIndex, position = 'end') {
      if (this.selectedSegmentIds.length === 0) return
      
      const selectedSegmentsToMove = []
      const updates = []
      
      // 1. Identify all selected segments across all groups and source trace
      this.groups.forEach((group, gIndex) => {
        const segments = group.segments || []
        const toKeep = []
        segments.forEach(s => {
          if (this.selectedSegmentIds.includes(s.id)) {
            selectedSegmentsToMove.push(s)
          } else {
            toKeep.push(s)
          }
        })
        if (toKeep.length !== segments.length) {
          updates.push({ gIndex, segments: toKeep })
        }
      })

      // Also check sourceTraceSegments (they might be selected but we usually clone them when moving to a group)
      if (this.sourceTraceSegments) {
        this.sourceTraceSegments.forEach(s => {
          if (this.selectedSegmentIds.includes(s.id)) {
             // If moving from source trace, we clone it to be safe (id collision, etc)
             selectedSegmentsToMove.push(this.cloneTraceSegment(s))
          }
        })
      }
      
      this.selectedSegmentIds = []
      
      if (selectedSegmentsToMove.length === 0) return

      // 2. Perform removals
      updates.forEach(({ gIndex, segments }) => {
        this.updateGroupSegments({ groupIndex: gIndex, segments, silent: true })
      })
      
      // 3. Add to target group
      const targetGroup = this.groups[targetGroupIndex]
      const currentTargetSegments = targetGroup.segments || []
      
      let finalSegments
      if (position === 'start') {
        finalSegments = [...selectedSegmentsToMove, ...currentTargetSegments]
      } else {
        finalSegments = [...currentTargetSegments, ...selectedSegmentsToMove]
      }
      
      this.updateGroupSegments({ groupIndex: targetGroupIndex, segments: finalSegments, silent: true })
      
      this.selectedSegmentIds = []
      this.expandedGroups[targetGroup.id] = true
    },

    async batchMoveToNewGroup() {
      if (this.selectedSegmentIds.length === 0) return
      const name = prompt('请输入新组名', `新组 ${this.groups.length + 1}`)
      if (name === null) return
      
      this.createGroup(name)
      
      this.$nextTick(() => {
        this.batchMoveToGroup(this.groups.length - 1)
      })
    },

    isGroupAllSelected(group) {
      if (!group.segments || group.segments.length === 0) return false;
      return group.segments.every(s => this.selectedSegmentIds.includes(s.id));
    },

    toggleGroupSelection(group) {
      const gIds = (group.segments || []).map(s => s.id);
      const allSelected = this.isGroupAllSelected(group);
      
      let newSelected = [...this.selectedSegmentIds];
      if (allSelected) {
        // Deselect all in this group
        newSelected = newSelected.filter(id => !gIds.includes(id));
      } else {
        // Select all in this group (avoid duplicates)
        gIds.forEach(id => {
          if (!newSelected.includes(id)) newSelected.push(id);
        });
      }
      this.selectedSegmentIds = newSelected;
    },

    updateSelectionRange(startLoc, endLoc) {
         const allWords = this.getAllWordsFlat()
         
         const findIdx = (target) => allWords.findIndex(item => 
             item.groupIndex === target.groupIndex && 
             item.segmentIndex === target.segmentIndex && 
             item.wordIndex === target.wordIndex
         )
         
         const startIdx = findIdx(startLoc)
         const endIdx = findIdx(endLoc)
         
         if (startIdx !== -1 && endIdx !== -1) {
             const min = Math.min(startIdx, endIdx)
             const max = Math.max(startIdx, endIdx)
             this.selectedWordLocations = allWords.slice(min, max + 1)
             this.selectedWordLocation = endLoc
         }
    },

    handleWordClick({ event, groupIndex, segmentIndex, wordIndex, word, segment }) {
        if (this.dragHasMoved) {
            this.dragHasMoved = false
            return
        }

        this.setActivePanel('pool')
        this.selectGroup(groupIndex)
        
        const loc = { groupIndex, segmentIndex, wordIndex }
        
        // Multi-selection logic
        if (event && (event.ctrlKey || event.metaKey)) {
             // Toggle
             const existingIdx = this.selectedWordLocations.findIndex(l => 
                 l.groupIndex === loc.groupIndex && 
                 l.segmentIndex === loc.segmentIndex && 
                 l.wordIndex === loc.wordIndex
             )
             if (existingIdx !== -1) {
                 this.selectedWordLocations.splice(existingIdx, 1)
             } else {
                 this.selectedWordLocations.push(loc)
             }
             this.selectedWordLocation = loc
        } else if (event && event.shiftKey && this.selectedWordLocation) {
             // Range Selection
             const allWords = this.getAllWordsFlat()
             
             // Helper to find index
             const findIdx = (target) => allWords.findIndex(item => 
                 item.groupIndex === target.groupIndex && 
                 item.segmentIndex === target.segmentIndex && 
                 item.wordIndex === target.wordIndex
             )
             
             const startIdx = findIdx(this.selectedWordLocation)
             const endIdx = findIdx(loc)
             
             if (startIdx !== -1 && endIdx !== -1) {
                 const min = Math.min(startIdx, endIdx)
                 const max = Math.max(startIdx, endIdx)
                 
                 // Replace selection with range
                 this.selectedWordLocations = allWords.slice(min, max + 1)
             } else {
                 // Fallback
                 this.selectedWordLocations = [loc]
                 this.selectedWordLocation = loc
             }
        } else {
             // Single select
             this.selectedWordLocations = [loc]
             this.selectedWordLocation = loc
        }
        
        // Update cache to point to this word to prevent cursor jumping to duplicates
        const qIdx = this.playbackQueue.findIndex(item => 
            item.groupIndex === groupIndex && 
            item.segmentIndex === segmentIndex && 
            item.wordIndex === wordIndex
        )
        if (qIdx !== -1) {
            this.cachedRangeIndex = qIdx
        }
        
        // Play from word start time
        const autoPlay = this.isPlaying

        const playPayload = {
            ...segment,
            start_time: Number(word.start_time),
            end_time: Number(word.end_time), // Optional
          autoPlay: autoPlay,
          targetPanel: 'pool'
        }
        this.$emit('play-segment', playPayload)
    },

    handleKeyDown(event) {
        if (!this.isFineTuning) return
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;
        
        // Backspace (8) specific for merge
        if (event.keyCode === 8 && this.canMerge) {
             this.mergeSegment();
             event.preventDefault();
             return;
        }

        // Delete key (46) or Backspace (8)
        if (event.keyCode === 46 || event.keyCode === 8) {
             if (this.selectedWordLocations.length > 0) {
                 this.deleteSelectedWords()
             } else if (this.selectedWordLocation) {
                 // Fallback to single cursor if no array selection (legacy safety)
                 this.toggleWordDeletion(this.selectedWordLocation, true)
             }
        }
        // Enter key (13)
        else if (event.keyCode === 13) {
             if (this.canSplit) {
                 this.splitSegment()
             }
        }
        // Esc key (27)
        else if (event.keyCode === 27) {
             this.selectedWordLocations = []
        }
    },

    batchUpdateWords(isDeleted) {
        if (this.selectedWordLocations.length === 0) return

        // Group by segment to minimize updates
        const updates = {} // key: "gIdx-sIdx", value: [wIdx, wIdx...]
        
        this.selectedWordLocations.forEach(loc => {
            const key = `${loc.groupIndex}-${loc.segmentIndex}`
            if (!updates[key]) updates[key] = []
            updates[key].push(loc.wordIndex)
        })
        
        for (const key in updates) {
            const [gIdx, sIdx] = key.split('-').map(Number)
            const wIndices = updates[key]
            
            const group = this.groups[gIdx]
            if (!group || !group.segments) continue
            const segment = group.segments[sIdx]
            if (!segment) continue
            
            const newSegment = JSON.parse(JSON.stringify(segment))
            let changed = false
            
            wIndices.forEach(wIdx => {
                if (newSegment.words && newSegment.words[wIdx]) {
                    if (!!newSegment.words[wIdx].deleted !== isDeleted) {
                        newSegment.words[wIdx].deleted = isDeleted
                        changed = true
                    }
                }
            })
            
            if (changed) {
                this.updateSegment({
                    groupIndex: gIdx,
                    segmentIndex: sIdx,
                    segment: newSegment
                })
            }
        }
    },

    deleteSelectedWords() {
        this.batchUpdateWords(true)
    },
    
    splitSegment(loc = null) {
        if (!loc && this.selectedWordLocations.length === 1) {
            loc = this.selectedWordLocations[0];
        }
        if (!loc) return;
        
        const { groupIndex, segmentIndex, wordIndex } = loc;
        
        const group = this.groups[groupIndex];
        if (!group || !group.segments) return;
        const segment = group.segments[segmentIndex];
        if (!segment || !segment.words || segment.words.length <= wordIndex) return;

        if (wordIndex === 0) {
            this.$store.commit('snackbar/show', {
                text: '无法在第一个字之前分裂片段',
                color: 'warning'
            });
            return;
        }

        const words1 = segment.words.slice(0, wordIndex);
        const words2 = segment.words.slice(wordIndex);
        
        const segment1 = {
            ...JSON.parse(JSON.stringify(segment)),
            id: `split-${Date.now()}-1`,
            words: words1,
            end_time: words1[words1.length - 1].end_time,
            text: words1.map(w => w.text || '').join('')
        };

        const segment2 = {
            ...JSON.parse(JSON.stringify(segment)),
            id: `split-${Date.now()}-2`,
            words: words2,
            start_time: words2[0].start_time,
            text: words2.map(w => w.text || '').join('')
        };

        const newSegments = [...group.segments];
        newSegments.splice(segmentIndex, 1, segment1, segment2);

        this.updateGroupSegments({
            groupIndex,
            segments: newSegments,
            silent: true
        });

        this.selectedWordLocations = [];
        this.selectedWordLocation = null;
    },

    mergeSegment(loc = null) {
        if (!loc && this.selectedWordLocations.length === 1) {
            loc = this.selectedWordLocations[0];
        }
        if (!loc) return;
        
        const { groupIndex, segmentIndex } = loc;
        if (segmentIndex === 0) return;
        
        const group = this.groups[groupIndex];
        if (!group || !group.segments) return;
        
        const prevSegment = group.segments[segmentIndex - 1];
        const currentSegment = group.segments[segmentIndex];
        
        const curId = currentSegment.video_id || currentSegment.transcription_id || currentSegment.id;
        const prevId = prevSegment.video_id || prevSegment.transcription_id || prevSegment.id;
        
        if (prevId !== curId) {
            this.$store.commit('snackbar/show', {
                text: '不同视频源的片段无法合并',
                color: 'warning'
            });
            return;
        }

        const mergedWords = [...(prevSegment.words || []), ...(currentSegment.words || [])];
        
        const mergedSegment = {
            ...JSON.parse(JSON.stringify(prevSegment)),
            words: mergedWords,
            end_time: currentSegment.end_time,
            text: mergedWords.map(w => w.text || '').join('')
        };

        const newSegments = [...group.segments];
        newSegments.splice(segmentIndex - 1, 2, mergedSegment);

        this.updateGroupSegments({
            groupIndex,
            segments: newSegments,
            silent: true
        });

        this.selectedWordLocations = [];
        this.selectedWordLocation = null;
    },

    restoreSelectedWords() {
        this.batchUpdateWords(false)
    },

    toggleWordDeletion({ groupIndex, segmentIndex, wordIndex }, isDeleted) {
      const group = this.groups[groupIndex]
      if (!group || !group.segments) return
      const segment = group.segments[segmentIndex]
      if (!segment || !segment.words || !segment.words[wordIndex]) return
      
      // Check if status is already what we want
      if (!!segment.words[wordIndex].deleted === isDeleted) return

      const newSegment = JSON.parse(JSON.stringify(segment))
      newSegment.words[wordIndex].deleted = isDeleted
      
      this.updateSegment({
        groupIndex: groupIndex,
        segmentIndex: segmentIndex,
        segment: newSegment
      })
    },

    /**
     * 将逐字数据合并为 Range 结构以减少传输体积
     */
    getOptimizedWords(words) {
      if (!words || words.length === 0) return []
      
      const optimized = []
      let currentRange = null

      for (const w of words) {
        if (!w.deleted) {
          if (currentRange && w.index_w === currentRange.end_index_w + 1) {
            // 连续，扩展当前范围
            currentRange.end_time = w.end_time
            currentRange.end_index_w = w.index_w
            currentRange.text += (w.text || "")
          } else {
            // 不连续，保存旧范围并开启新范围
            if (currentRange) optimized.push(currentRange)
            currentRange = {
              text: w.text || "",
              start_time: w.start_time,
              end_time: w.end_time,
              start_index_w: w.index_w,
              end_index_w: w.index_w,
              index_s: w.index_s,
              index_p: w.index_p,
              deleted: false
            }
          }
        } else {
          // 已删除，断开当前范围
          if (currentRange) {
            optimized.push(currentRange)
            currentRange = null
          }
        }
      }
      if (currentRange) optimized.push(currentRange)
      return optimized
    },
    
    formatTime(ms) {
      if (!ms && ms !== 0) return '00:00'
      const s = Math.floor(ms / 1000)
      const m = Math.floor(s / 60)
      const sec = s % 60
      return `${m}:${sec.toString().padStart(2, '0')}`
    },
    
    calculateSegmentDuration(s) {
      if (s.words && s.words.length > 0) {
        return s.words.reduce((acc, w) => {
          if (!w.deleted) {
            return acc + (Number(w.end_time) - Number(w.start_time));
          }
          return acc;
        }, 0);
      }
      return Number(s.end_time) - Number(s.start_time);
    },

    calculateGroupDuration(group) {
      if (!group.segments) return 0;
      return group.segments.reduce((acc, s) => acc + this.calculateSegmentDuration(s), 0);
    },

    getGroupDurationInfo(group) {
      const groupDur = this.calculateGroupDuration(group);
      return this.formatTime(groupDur);
    },
    
    exportAll() {
      if (this.totalSegments === 0) return
      this.showExportOptions = true
    },

    exportSingleGroup(group) {
        if (!group.segments || group.segments.length === 0) {
            this.$store.commit('snackbar/show', {
                text: '该分组内没有片段',
                color: 'warning'
            })
            return
        }
        this.groupToExport = group;
        this.showSingleGroupExportDialog = true;
    },

    async handleCopyAllText() {
        if (this.totalSegments === 0) return;
        
        const allText = this.groups
            .flatMap(g => (g.segments || []))
            .map(s => s.text || '')
            .filter(t => t.trim().length > 0)
            .join('\n');
            
        if (await this.copyTextToClipboard(allText)) {
            this.showExportOptions = false;
        }
    },

    async handleCopyGroupText() {
        if (!this.groupToExport || !this.groupToExport.segments) return;
        
        const fullText = this.groupToExport.segments
            .map(s => s.text || '')
            .filter(t => t.trim().length > 0)
            .join('\n');
            
        if (await this.copyTextToClipboard(fullText)) {
            this.showSingleGroupExportDialog = false;
        }
    },

    async copyTextToClipboard(text) {
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                // Fallback for non-secure contexts (HTTP via IP)
                const textArea = document.createElement("textarea");
                textArea.value = text;
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                textArea.style.top = "0";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                if (!successful) throw new Error('execCommand failed');
            }
            
            this.$store.commit('snackbar/show', {
                text: '文案已复制到剪贴板',
                color: 'success'
            });
            return true;
        } catch (err) {
            console.error('Failed to copy text: ', err);
            this.$store.commit('snackbar/show', {
                text: '复制失败，请尝试在浏览器地址栏使用 localhost 或开启 HTTPS',
                color: 'error'
            });
            return false;
        }
    },

    handleExportGroupVideo() {
        if (!this.groupToExport) return;
        this.submitExport(false, [this.groupToExport]);
        this.showSingleGroupExportDialog = false;
    },

    async confirmExport(merge) {
      this.showExportOptions = false
      await this.submitExport(merge)
    },

    async submitExport(merge = false, specificGroups = null) {
      const groupsToExport = specificGroups || this.groups
      const totalSegs = groupsToExport.reduce((acc, g) => acc + (g.segments ? g.segments.length : 0), 0)
      if (totalSegs === 0) return
      
      this.isExporting = true
      try {
        const payload = {
          project_id: `proj_${Date.now()}`,
          merge_groups: merge,
          auto_flip: this.autoFlip,
          audio_norm: this.audioNorm,
          remark: merge ? this.exportRemark : null,
          groups: groupsToExport.map(g => ({
            name: g.name,
            segments: (g.segments || []).map(s => ({
              video_id: s.video_id || s.id,
              transcription_id: s.transcription_id,
              start_time: Number(s.start_time),
              end_time: Number(s.end_time),
              text: s.text,
              video_url: s.video_url,
              commodity_name: s.commodity_name,
              words: this.getOptimizedWords(s.words)
            }))
          }))
        }
        
        /*
        console.log('Exporting payload:', JSON.stringify(payload, null, 2))
        const res = await create_batch_slices(payload)
        console.log('Export response:', res)
        */
        const res = await create_batch_slices(payload)
        
        if (res.task_ids && res.task_ids.length > 0) {
            let taskIndex = 0
            this.exportTasks = res.task_ids.map((id) => {
                taskIndex++
                return {
                    id: id,
                    status: 'pending',
                    message: '排队中...',
                    file_url: null,
                    name: `片段 ${taskIndex}`
                }
            })
            this.showExportOverlay = true
            this.startPolling()
            
            this.$store.commit('snackbar/show', {
              text: `已提交 ${res.task_ids.length} 个剪辑任务，正在处理...`,
              color: 'info'
            })
        } else {
            // Show success message (using store snackbar if available)
            this.$store.commit('snackbar/show', {
              text: `成功提交 ${res.task_ids ? res.task_ids.length : 0} 个剪辑任务`,
              color: 'success'
            })
        }
        
        // Optional: clear pool or navigate
      } catch (e) {
        console.error(e)
        this.$store.commit('snackbar/show', {
          text: '导出失败: ' + (e.msg || e.response?.data?.detail || e.message || '未知错误'),
          color: 'error'
        })
      } finally {
        this.isExporting = false
      }
    },

    startPolling() {
        if (this.pollingTimer) clearInterval(this.pollingTimer)
        this.pollingTimer = setInterval(this.checkAllTasks, 3000)
    },

    async checkAllTasks() {
        let allDone = true
        for (const task of this.exportTasks) {
            if (task.status !== 'completed' && task.status !== 'failed') {
                allDone = false
                try {
                    const response = await axios.get(BASE_URL + `slice_status/${task.id}`)
                    // Handle response format.
                    const data = response
                    
                    if (data.status) {
                        task.status = data.status
                        if (data.status === 'completed') {
                            task.file_url = data.file_url
                            task.message = '完成'
                        } else if (data.status === 'failed') {
                            task.status = 'failed'
                            task.message = data.error_message || '失败'
                        } else {
                            task.message = data.status === 'processing' ? '处理中...' : '排队中...'
                        }
                    }
                } catch (e) {
                    console.error(e)
                    // Don't mark as failed immediately on network error, maybe retry?
                    // But for now let's keep previous status or mark failed if persistent
                }
            }
        }
        
        if (allDone) {
            clearInterval(this.pollingTimer)
            this.pollingTimer = null
            // 导出任务全部成功完成后，清空当前窗口的剪辑草稿
            const allCompleted = this.exportTasks.every(t => t.status === 'completed')
            if (allCompleted) {
                this.$store.dispatch('multiClipper/clearDraft')
                this.$store.commit('snackbar/show', {
                    text: '所有导出任务已完成',
                    color: 'success'
                })
            } else {
                this.$store.commit('snackbar/show', {
                    text: '部分导出任务失败，剪辑草稿已保留',
                    color: 'warning'
                })
            }
        }
    },

    closeExportOverlay() {
        this.showExportOverlay = false
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer)
            this.pollingTimer = null
        }
        // Don't clear exportTasks immediately so user can see them if they reopen? 
        // But we don't have a reopen button. So clearing is fine.
        // Or maybe we should keep them if background polling was supported, but for now we stop.
        this.exportTasks = []
    },

    downloadFile(url) {
        window.open(url, '_blank')
    },

    convertTraceToSegments(source, urlMap = {}) {
       if (!source || !source.source_trace) return []
       return source.source_trace.map((trace, idx) => {
         // Reverted: Use trace source ID primarily as these are raw materials
         const sourceId = trace.source_from || trace.video_id || source.video_id
         
         const parseTime = (val) => {
             if (val === undefined || val === null) return undefined
             const num = Number(val)
             if (isNaN(num)) return undefined
             return num
         }

         let startTime = 0
         let endTime = 0
         
         if (trace.source_spans && trace.source_spans.length > 0) {
             const firstSpan = trace.source_spans[0]
             const lastSpan = trace.source_spans[trace.source_spans.length - 1]
             
             const fsStart = parseTime(firstSpan.start_time)
             const fsStartSec = parseTime(firstSpan.start)
             
             if (fsStart !== undefined) startTime = fsStart
             else if (fsStartSec !== undefined) startTime = fsStartSec * 1000
             
             const lsEnd = parseTime(lastSpan.end_time)
             const lsEndSec = parseTime(lastSpan.end)
             
             if (lsEnd !== undefined) endTime = lsEnd
             else if (lsEndSec !== undefined) endTime = lsEndSec * 1000
         } 
         
         if (startTime === 0 && endTime === 0) {
             const tStart = parseTime(trace.start_time)
             const tStartSec = parseTime(trace.start) || parseTime(trace.startTime)
             if (tStart !== undefined) startTime = tStart
             else if (tStartSec !== undefined) startTime = tStartSec
             
             const tEnd = parseTime(trace.end_time)
             const tEndSec = parseTime(trace.end) || parseTime(trace.endTime)
             if (tEnd !== undefined) endTime = tEnd
             else if (tEndSec !== undefined) endTime = tEndSec

             if (startTime < 100000 && startTime > 0) startTime = startTime * 1000
             if (endTime < 100000 && endTime > 0) endTime = endTime * 1000
         }

         // Resolve URL: use current video's URL if IDs match, otherwise check urlMap
         let videoUrl = null
         if (sourceId === source.video_id || sourceId === source.id) {
             videoUrl = source.video_url || (source.urls ? source.urls[0] : null)
         } else if (urlMap && urlMap[sourceId]) {
             videoUrl = urlMap[sourceId]
         } else if (trace.video_url || trace.url) {
             videoUrl = trace.video_url || trace.url
         }
         
         return {
           ...trace,
           id: `manual-trace-${Date.now()}-${idx}`,
           text: trace.text || trace.origin_text || `片段 ${idx + 1}`,
           start_time: startTime,
           end_time: endTime,
           video_name: trace.source_title || trace.title || trace.text || `片段 ${idx + 1}`,
           video_id: sourceId,
           video_url: videoUrl,
           transcription_id: trace.transcription_id || sourceId, 
           transcription_text: null, 
           words: trace.source_spans ? trace.source_spans.map(w => {
               const wStart = parseTime(w.start_time)
               const wStartSec = parseTime(w.start)
               const wEnd = parseTime(w.end_time)
               const wEndSec = parseTime(w.end)
               
               return {
                   ...w,
                   text: w.text || w.word,
                   start_time: wStart !== undefined ? wStart : (wStartSec !== undefined ? wStartSec * 1000 : 0),
                   end_time: wEnd !== undefined ? wEnd : (wEndSec !== undefined ? wEndSec * 1000 : 0)
               }
           }) : [],
           deleted: false
         }
       })
    }
  },
  watch: {
    previewSource: {
        handler(newSource) {
            if (newSource && newSource.source_trace && newSource.source_trace.length > 0) {
                // Fetch URLs for source traces to prevent playback stutter due to missing URLs
                const urlMap = {}
                // No longer need to fetch URLs separately as server provides them
                
                const segments = this.convertTraceToSegments(newSource, urlMap)
                if (segments.length > 0) {
                    const newGroup = {
                        id: `group-${Date.now()}`,
                        name: newSource.title || `溯源转手动-${new Date().toLocaleTimeString()}`,
                        segments: segments
                    }
                    // Clone existing groups and add new one
                    const updatedGroups = this.groups ? [...this.groups] : []
                    updatedGroups.push(newGroup)
                    this.localGroups = updatedGroups
                    
                    this.setActivePanel('pool')
                }
            }
        },
        immediate: true
    },
    currentTime(newTime) {
      if (Math.abs(newTime - this.lastPoolTime) > 0.5) {
        this.lastPoolTime = newTime
      }
      if (this.isPlaying) {
        this.handleSequencePlayback(newTime)
      }
    },
    groups: {
      handler(newGroups) {
        // Log manual clip structure for comparison as requested by user
        if (newGroups && newGroups.length > 0 && newGroups[0].segments && newGroups[0].segments.length > 0) {
            // Log only once or when first segment changes to avoid spam
            // But user wants to "pull comparison", so let's just log it.
            // Use console.log with a distinct prefix
            // console.log('Pool Clip Sample (Manual):', JSON.stringify(newGroups[0].segments[0], null, 2))
        }

        // Try to preserve playback position by tracking current segment ID
        const lastItem = (this.cachedRangeIndex >= 0 && this.playbackQueue[this.cachedRangeIndex]) 
            ? this.playbackQueue[this.cachedRangeIndex] 
            : null

        const lastSegmentId = lastItem ? lastItem.segment.id : null
        const lastWordIndex = lastItem ? lastItem.wordIndex : null

        // Reset cache temporarily
        this.cachedRangeIndex = -1

        if (lastSegmentId) {
            this.$nextTick(() => {
                // Try to find the exact word range if possible, otherwise just the segment
                const newIdx = this.playbackQueue.findIndex(r => 
                    r.segment.id === lastSegmentId && 
                    (lastWordIndex === null || r.wordIndex === lastWordIndex)
                )
                
                if (newIdx !== -1) {
                    this.cachedRangeIndex = newIdx
                } else {
                    // Fallback to just segment ID
                    const segIdx = this.playbackQueue.findIndex(r => r.segment.id === lastSegmentId)
                    if (segIdx !== -1) this.cachedRangeIndex = segIdx
                }
            })
        }

        const newCounts = {}
        if (newGroups) {
            newGroups.forEach(group => {
            const prevCount = this.prevGroupSegmentCounts[group.id] || 0
            if (group.segments && group.segments.length > prevCount) {
                this.expandedGroups[group.id] = true
            }
            newCounts[group.id] = group.segments ? group.segments.length : 0
            })

            // Sync highlight ranges if highlighted group changed
            if (this.highlightedGroupId) {
                const hGroup = newGroups.find(g => g.id === this.highlightedGroupId)
                if (hGroup) {
                    const ranges = (hGroup.segments || []).map(s => ({
                        start_time: s.start_time,
                        end_time: s.end_time,
                        video_id: s.video_id || s.id
                    }))
                    this.setHighlightRanges(ranges)
                } else {
                    this.highlightedGroupId = null
                    this.setHighlightRanges([])
                }
            }
        }
        this.prevGroupSegmentCounts = newCounts
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style scoped>
.pool-footer {
  padding: 10px;
  border-top: 1px solid #ddd;
  background: #f9f9f9;
  display: flex;
  justify-content: center;
}

.ai-btn {
  width: 100%;
  padding: 10px;
  font-size: 1.1em;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(118, 75, 162, 0.2);
  transition: all 0.3s ease;
}

.ai-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 8px rgba(118, 75, 162, 0.3);
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.ai-btn:disabled {
  background: #ccc;
  box-shadow: none;
}

.kol-select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.kol-select:focus {
  border-color: #2196f3;
}

/* Scrollbar Styling for ClipPool */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb:hover {
  background: #bbb;
}

/* Source Trace Styles */
.active-mode-border {
    border: 2px solid #f44336 !important;
    box-shadow: 0 0 8px rgba(244, 67, 54, 0.3);
    position: relative;
    z-index: 1;
}

.source-trace-group {
    border: 2px solid #ffe0b2;
    margin-bottom: 15px !important;
    transition: all 0.3s ease;
}

.trace-header {
    background-color: #fff3e0 !important;
}

.trace-title {
    font-weight: bold;
    color: #e65100;
}

.trace-list {
    /* max-height: 250px;  Removed to avoid nested scrollbars, handled by DraggableGroup */
    /* overflow-y: auto; */
    background-color: #fff8e1;
}

.clip-pool {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pool-header-wrapper {
  border-bottom: 1px solid #ddd;
  background: #fff;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px;
  gap: 10px;
}

.header h3 {
  margin: 0;
  font-size: 1.1em;
  flex-shrink: 0;
  white-space: nowrap;
}

.header-actions {
  display: flex;
  gap: 6px;
  row-gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.action-btn {
  height: 28px;
  padding: 0 10px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
}

.action-btn.ai-btn:hover:not(:disabled) {
  background-color: #f3e5f5 !important;
  opacity: 0.9;
}

.ai-dropdown-container {
  display: inline-block;
  position: relative;
}

.ai-dropdown-container:hover .dropdown-content {
  display: block;
}

.action-btn.ai-btn {
  background-color: #f3e5f5;
  color: #7b1fa2;
  border: 1px solid #ce93d8;
}

.ai-dropdown-container .dropdown-content {
  right: 0;
  min-width: 140px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  border: 1px solid #eee;
}

.ai-dropdown-container .dropdown-content a {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #444;
  font-size: 13px;
  transition: background 0.2s;
  cursor: pointer;
}

.ai-dropdown-container .dropdown-content a i {
  color: #7b1fa2;
  font-size: 16px;
}

.ai-dropdown-container .dropdown-content a:hover {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

/* AI Params Popover Styles */
.ai-params-popover {
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid #e1bee7;
  z-index: 2000;
  margin-top: 8px;
  overflow: hidden;
  animation: popover-in 0.2s ease-out;
}

.disassemble-popover {
  right: -100px; /* 向左偏移一点，防止靠右太近 */
  top: 35px;
  transform-origin: top center;
}

@keyframes popover-in {
  from { transform: translateY(-10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.popover-header {
  padding: 10px 12px;
  background: #f3e5f5;
  color: #7b1fa2;
  font-size: 13px;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.popover-header i.mdi-close {
  cursor: pointer;
  font-size: 16px;
  opacity: 0.7;
}

.popover-header i.mdi-close:hover {
  opacity: 1;
}

.popover-body {
  padding: 15px;
}

.popover-body label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
}

.popover-body .commodity-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
}

.popover-body .commodity-input:focus {
  border-color: #7b1fa2;
}

.popover-body .hint {
  font-size: 11px;
  color: #999;
  margin-top: 8px;
}

.popover-footer {
  padding: 10px 15px;
  background: #f9f9f9;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.popover-footer button {
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.popover-footer .cancel-btn {
  background: white;
  border: 1px solid #ddd;
  color: #666;
}

.popover-footer .confirm-btn {
  background: #7b1fa2;
  border: none;
  color: white;
}

.title-item-hover:hover {
  background-color: #fff !important;
  border-color: #ce93d8 !important;
  box-shadow: 0 2px 8px rgba(123, 31, 162, 0.1);
}

.copy-btn-hover:hover {
  border-color: #7b1fa2 !important;
  color: #7b1fa2 !important;
  background-color: #f3e5f5 !important;
}

.action-btn:hover {
  background: #f5f5f5;
}

.action-btn.active-mode {
  background: #e3f2fd;
  color: #1976d2;
  border-color: #1976d2;
}

.action-btn.primary {
  background: #2196f3;
  color: white;
  border-color: #2196f3;
}

.action-btn.primary:hover {
  background: #1976d2;
}

.action-btn.danger {
  color: #d32f2f;
  border-color: #f3c2c2;
}

.action-btn.danger:hover:not(:disabled) {
  background: #fdecea;
  border-color: #d32f2f;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* AI Titles Dialog Styles */
.ai-titles-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.ai-titles-dialog {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 500px;
  overflow: hidden;
  animation: dialog-slide-up 0.3s ease-out;
}

@keyframes dialog-slide-up {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.dialog-header {
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dialog-header .close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
}

.dialog-content {
  padding: 24px;
}

.dialog-footer {
  padding: 16px 20px;
  background: #f8f9fa;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.secondary-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 14px;
}

.primary-btn {
  padding: 8px 24px;
  background: #7b1fa2;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.commodity-input {
  width: 100%;
  border: 1px solid #ce93d8;
  padding: 12px;
  border-radius: 8px;
  outline: none;
  font-size: 14px;
  transition: all 0.2s;
}

.commodity-input:focus {
  border-color: #7b1fa2;
  box-shadow: 0 0 0 3px rgba(123, 31, 162, 0.1);
}

.popover-header {
  padding: 12px 16px;
  background: #f3e5f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  color: #7b1fa2;
}

.popover-body {
  padding: 16px;
}

.popover-body label {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  display: block;
}

.popover-body .hint {
  font-size: 12px;
  color: #999;
  margin-top: 12px;
  line-height: 1.4;
}

.popover-footer {
  padding: 12px 16px;
  background: #f9f9f9;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #eee;
}

.cancel-btn {
  padding: 6px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
}

.confirm-btn {
  padding: 6px 16px;
  background: #7b1fa2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fine-tuning-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  background-color: #e3f2fd;
  border-top: 1px solid #bbdefb;
}

.selection-count {
  font-size: 0.9em;
  color: #1976d2;
  font-weight: 500;
}

.ft-actions {
  display: flex;
  gap: 8px;
}

.ft-btn {
  background: white;
  border: 1px solid #bbdefb;
  color: #1976d2;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  font-size: 0.9em;
  display: flex;
  align-items: center;
  gap: 4px;
}

.ft-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
  color: #aaa;
  border-color: #ddd;
}

.ft-btn:hover:not(:disabled) {
  background: #bbdefb;
}

.ft-btn.delete {
  color: #d32f2f;
  border-color: #ffcdd2;
}

.ft-btn.delete:hover:not(:disabled) {
  background: #ffcdd2;
}

.groups-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

/* 重复素材提示横幅 */
.dup-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 6px;
  color: #ad6800;
  font-size: 12px;
  line-height: 1.5;
}
.dup-banner i {
  color: #ff9800;
  font-size: 16px;
}
.dup-banner b {
  color: #d4380d;
}
.dup-banner em {
  font-style: normal;
  color: #d4380d;
  font-weight: bold;
  padding: 0 3px;
  border-radius: 3px;
  background: #ffd591;
}

.empty-pool {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #999;
  border: 2px dashed #eee;
  border-radius: 8px;
}

.export-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.export-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.export-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}
.close-btn {
  background: none;
  border: none;
  font-size: 1.5em;
  cursor: pointer;
}
.task-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.task-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}
.task-status.pending, .task-status.processing {
  color: #1976d2;
}
.task-status.completed {
  color: #4caf50;
}
.task-status.failed {
  color: #f44336;
}
.download-btn {
  padding: 4px 8px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.download-btn:hover {
  background: #1976d2;
}

.group-item {
  margin-bottom: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  /* overflow: hidden; */ /* 允许 AI 弹出框溢出显示 */
}

.group-item.active {
  border-color: #2196f3;
  box-shadow: 0 0 0 1px #2196f3;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  user-select: none;
  min-height: 32px;
  flex-wrap: nowrap;
  box-sizing: border-box;
  width: 100%;
}

.group-header:hover {
  background: #eee;
}

/* Help Modal Styles */
.help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.help-modal {
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.help-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.help-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.help-content {
  padding: 20px;
  overflow-y: auto;
}

.help-step {
  display: flex;
  margin-bottom: 20px;
}

.help-step:last-child {
  margin-bottom: 0;
}

.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e3f2fd;
  color: #1976d2;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;
  flex-shrink: 0;
  font-size: 20px;
}

.step-info h4 {
  margin: 0 0 5px 0;
  font-size: 16px;
  color: #333;
}

.step-info p {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.help-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.primary-btn {
  background: #2196f3;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.primary-btn:hover {
  background: #1976d2;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.group-drag-handle {
  cursor: grab;
  color: #aaa;
  margin-right: 4px;
  font-size: 1.2em;
}

.group-drag-handle:active {
  cursor: grabbing;
}

.collapse-icon {
  font-size: 1.2em;
  color: #666;
}

.group-name-input {
  border: none;
  background: transparent;
  font-weight: bold;
  font-size: 0.95em;
  min-width: 30px;
  max-width: 150px;
  flex: 1;
  resize: none;
  overflow: hidden;
  font-family: inherit;
  padding: 2px 4px;
  line-height: 1.2;
}

.ai-disassemble-btn {
  /* margin-left: auto; remove this if any */
}

.ai-disassembly-result {
  background: #fdfbff;
  border: 1px dashed #b19cd9;
  border-radius: 4px;
  margin: 5px 8px;
  padding: 8px;
  font-size: 0.9em;
}

.disassembly-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
}

.disassembly-title {
  font-weight: bold;
  color: #673ab7;
  display: flex;
  align-items: center;
  gap: 4px;
}

.disassembly-title::before {
  content: "\F009D"; /* brain icon in mdi font if available, or just use icon in template */
  font-family: "Material Design Icons";
}

.close-dis-btn {
  font-size: 0.8em;
  color: #666;
  cursor: pointer;
  background: #eee;
  border: none;
  padding: 2px 6px;
  border-radius: 3px;
}

.ai-reference-group {
  border: 1px solid #d1c4e9;
  background-color: #f8f7ff;
}

.ai-ref-header {
  background-color: #ede7f6 !important;
  border-bottom: 1px solid #d1c4e9 !important;
}

.ai-ref-title {
  font-weight: bold;
  color: #5e35b1;
  font-size: 0.9em;
}

.read-only-list {
  background: #fdfbff;
  opacity: 0.9;
}

.dis-category {
  margin-bottom: 10px;
}

.category-name {
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
  font-size: 0.95em;
  background: #eee;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
}

.category-quotes {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 8px;
}

.quote-item {
  color: #555;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  background: white;
  border: 1px solid #f0f0f0;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  transition: all 0.2s;
}

.quote-item:hover {
  background: #f3e5f5;
  border-color: #ce93d8;
  color: #4a148c;
}

.quote-play-icon {
  color: #673ab7;
  font-size: 1.1em;
}

.quote-text {
  line-height: 1.4;
}

.group-name-input:focus {
  outline: none;
  background: #fff;
  border-radius: 4px;
}

.highlight-btn {
  padding: 2px 6px;
  border: 1px solid transparent;
  background: transparent;
}

.group-export-btn {
  padding: 2px 8px;
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  white-space: nowrap;
}

.group-export-btn:hover {
  background-color: #c8e6c9;
  border-color: #81c784;
}

.header-icon-btn {
  padding: 4px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 1.1em;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-left: 2px;
}

.header-icon-btn:hover {
  background: #e0e0e0;
}

.ai-disassemble-btn {
  color: #673ab7;
}

.ai-disassemble-btn:hover {
  background: #ede7f6;
  color: #4a148c;
}

.ft-toggle-btn {
  color: #2196f3;
}

.ft-toggle-btn:hover {
  background: #e3f2fd;
}

.active-ft {
  background: #2196f3 !important;
  color: white !important;
}

.header-icon-btn:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.group-export-btn i {
  font-size: 1.1em;
}

.highlight-btn:hover {
  background: #e0e0e0;
}

.active-highlight {
  color: #ff9800;
  background: #fff3e0;
  border-color: #ffb74d;
}

.active-highlight:hover {
  background: #ffe0b2;
}

.group-count {
  font-size: 0.85em;
  color: #888;
}

.delete-btn {
  color: #999;
  font-size: 1.1em;
  margin-left: 4px;
}

.delete-btn:hover {
  color: #ff5252;
  background-color: #ffebee;
}

.ghost-group {
  opacity: 0.5;
  background: #e1f5fe;
  border: 1px dashed #29b6f6;
}

.segments-list {
  padding: 8px;
  min-height: 50px;
}

.segment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid #eee;
  font-size: 0.9em;
}

.segment-item:last-child {
  border-bottom: none;
}

.segment-info {
  flex: 1;
  overflow: hidden;
}

.seg-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

.seg-time {
  font-size: 0.8em;
  color: #888;
}

.empty-group-hint {
  padding: 15px;
  text-align: center;
  color: #ccc;
  font-size: 0.9em;
}

.trace-info-panel {
  margin: 10px;
  border: 1px solid #ffcc80;
  background-color: #fff3e0;
  border-radius: 4px;
  overflow: hidden;
}

.trace-header {
  padding: 8px 12px;
  background-color: #ffe0b2;
  font-weight: 500;
  font-size: 0.9em;
  color: #ef6c00;
  display: flex;
  align-items: center;
  gap: 6px;
}

.trace-content-wrapper {
  /* max-height: 200px; Removed to avoid nested scrollbars */
  /* overflow-y: auto; */
  padding: 5px;
}

.batch-segment-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #e3f2fd;
  border-bottom: 1px solid #bbdefb;
}

.batch-actions {
  display: flex;
  gap: 8px;
}

.batch-btn {
  padding: 4px 10px;
  font-size: 13px;
  border-radius: 4px;
  background: white;
  border: 1px solid #2196f3;
  color: #2196f3;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.batch-btn:hover {
  background: #f1f8fe;
}

.batch-btn.delete {
  border-color: #f44336;
  color: #f44336;
}

.batch-btn.delete:hover {
  background: #ffebee;
}

/* Dropdown */
.move-dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-content {
  display: none;
  position: absolute;
  background-color: #f9f9f9;
  min-width: 240px;
  box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
  z-index: 100;
  border-radius: 4px;
  right: 0;
}

.dropdown-content a {
  color: #333;
  padding: 8px 12px;
  text-decoration: none;
  display: block;
  font-size: 13px;
  cursor: pointer;
}

.dropdown-content a:hover {
  background-color: #f1f1f1;
}

.group-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  gap: 20px;
}

.group-name-label {
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  font-weight: 500;
}

.move-sub-options {
  display: flex;
  gap: 8px;
}

.move-sub-options a {
  padding: 4px 10px !important;
  font-size: 12px !important;
  background: #f0f0f0;
  border-radius: 4px;
  color: #444 !important;
}

.move-sub-options a:hover {
  background: #2196f3 !important;
  color: white !important;
}

.move-dropdown:hover .dropdown-content {
  display: block;
}
</style>